import rawsToml from "../wasm/materials.toml?raw";
import init, {
  beat_surface_ids,
  beat_surface_name,
  body_scale_permille,
  chunk_size,
  commodity_name,
  feature_kind_ids,
  beckon_reach_tiles,
  heal_days_at_slowest,
  bow_range_tiles,
  item_kind_ids,
  item_kind_name,
  item_noun,
  item_portable,
  item_shape_ids,
  job_kind_ids,
  job_kind_name,
  life_stage_fights,
  life_stage_portable,
  life_stage_word,
  loose_cooldown_ticks,
  lot_kind_name,
  max_carried,
  max_chronicle_entries,
  max_companions,
  max_claims,
  max_origins,
  max_player_name_bytes,
  strike_cooldown_ticks,
  strike_reach_tiles,
  max_saga_eras,
  max_saga_line_cites,
  origin_kind_ids,
  origin_kind_name,
  price_move_name,
  ramp_shape_id,
  stair_shape_id,
  stock_band_name,
  walk_ticks_per_step,
  walk_ticks_per_step_for_stage,
  WasmSim,
  world_z_max,
  world_z_min,
} from "../wasm/sim_wasm.js";
import {
  type ColumnSpan,
  forEachChunkNear,
  INTEREST_RADIUS_CHUNKS,
  MeshWindow,
} from "./streaming";
import {
  type AftermathFeature,
  BAND_DEAR,
  BAND_FAMINE,
  BAND_PLENTY,
  BAND_STEADY,
  BEAT_BOARD,
  BEAT_GROUND,
  BEAT_MOUTH,
  BEAT_ROAD,
  CHUNK_SIZE,
  type ChronicleView,
  type ChunkCoord,
  type ChunkMeshUpdate,
  chunkKey,
  type EventId,
  FEATURE_BATTLEFIELD,
  FEATURE_BURNED_FARMSTEAD,
  FEATURE_GRAVE,
  FEATURE_MARKET_BOARD,
  type CarriedItem,
  type FigureId,
  floorDiv,
  ITEM_BAR,
  ITEM_CORPSE,
  ITEM_BOULDER,
  ITEM_FOOD,
  ITEM_PLANT,
  ITEM_WOOD,
  JOB_BURY,
  JOB_CARVE,
  JOB_COOK,
  JOB_FELL,
  JOB_FORGE,
  JOB_HARVEST,
  JOB_INTER,
  JOB_MINE,
  JOB_PATROL,
  JOB_TRANSCRIBE,
  JOB_WRITE,
  type JobPost,
  type MarketRow,
  MAX_CARRIED,
  MAX_COMPANIONS,
  BECKON_REACH_TILES,
  HEAL_DAYS_AT_SLOWEST,
  ORIGIN_HEIR,
  BOW_RANGE_TILES,
  LOOSE_COOLDOWN_TICKS,
  STRIKE_COOLDOWN_TICKS,
  STRIKE_REACH_TILES,
  MAX_CHRONICLE_ENTRIES,
  MAX_CLAIMS,
  MAX_ORIGINS,
  MAX_PLAYER_NAME_BYTES,
  MAX_SAGA_ERAS,
  MAX_SAGA_LINE_CITES,
  ORIGIN_BOUND,
  type OriginCard,
  type OriginClaim,
  ORIGIN_ORPHAN,
  ORIGIN_UNRECORDED,
  type PresentFigure,
  type RefugeeGroup,
  type RosterEntry,
  type RouteLeg,
  type RuinView,
  type RumorEntry,
  type SagaEra,
  type SagaLine,
  type SettlementId,
  type SettlementView,
  type ShelvedBook,
  type SpokenLine,
  type SpotlightBeat,
  type TerrainSampler,
  TILE_SHAPE_RAMP,
  TILE_SHAPE_STAIR_UP,
  type TilePos,
  type TownLot,
  type TradePulse,
  type WitnessSource,
  type WorldDateView,
  type WorldSource,
  type WorldStats,
} from "./types";

/*
 * The simulation, as the client is allowed to see it — over the real thing.
 *
 * This file is the whole of the client's knowledge of `sim-wasm`. Above it
 * nothing imports the generated package, and nothing above it decides anything
 * about the world: intentions go in as commands (`setInterest`, `dig`, since
 * Phase 5 `claim`, `tell` and `placeChronicle`, and since Phase 6 the three
 * `queueBorn*` doors), and what comes back out is
 * snapshots, mesh buffers and events (devlog #2, plan decision 6). The rule is worth restating exactly here, because this is the one file
 * where breaking it would be easy and invisible — the sim's queries are right
 * there, and answering a gameplay question in JavaScript costs one line.
 *
 * What this file *does* decide is renderer business, and there are three pieces
 * of it:
 *
 * - **Which chunks to mesh** (`world/streaming.ts`). The simulation generates
 *   whole 88-level columns and reports every chunk that changed; a first-person
 *   client can see a fraction of that. Narrowing it is explicitly the host's
 *   call (`TickSummary::dirty_chunks`).
 * - **What an ungenerated tile means to a falling body.** The sim says "no chunk
 *   here" and nothing more; the client reads that as solid so the player does
 *   not fall through the streaming frontier (`TerrainSampler.isSolid`).
 * - **When to repeat an idempotent command.** Interest is submitted only when it
 *   changes, which keeps the recorded command log sparse — an entry per tick
 *   would be 27 MB per 100k ticks of a file that is otherwise 2.5.
 *
 * # Lifetimes
 *
 * Every value the boundary hands back is a wasm-bindgen object with a `free()`,
 * and the two hot ones — the tick summary and a chunk mesh — are freed here, in
 * the same function that made them. The buffers are copies by the time they
 * leave (`ChunkMesh`'s four methods each allocate a fresh typed array), so
 * nothing downstream holds a view into linear memory: that is the copy-out rule
 * from `docs/research/rendering/threejs-chunks.md` §2, and it is the reason a
 * detached-buffer bug cannot happen here.
 */

/** The seed used when the URL does not name one. `sim-core`'s golden seed. */
export const DEFAULT_SEED = 0x5eedn;

/** The URL parameter that overrides it: `?seed=1234` or `?seed=0xdeadbeef`. */
const SEED_PARAM = "seed";

/**
 * How many years of history a session stands on when the URL does not say: 200.
 *
 * **Chosen by measurement** (plan decision 1). The bootstrap is a day-stepping
 * fast-forward inside construction: it costs wall time once, before the first
 * frame, and nothing afterwards — a tick in a two-century world measures the
 * same 0.19 ms as a tick in an empty one, because the abstract tier works on day
 * boundaries and a tick's cost is the map's hash.
 *
 * Measured against the shipped release build, one construction per fresh Node
 * process, which is the shape of a page load (the *second* construction in a
 * process is three to four times faster, because the engine has tiered the
 * module up by then — that number would flatter the browser and is not the one
 * below). Median of five, and noisy:
 *
 * ```text
 *   25 years     24 ms   1 settlement                    nothing has happened
 *   50 years    103 ms   8 settlements,  89 records      graves, no news
 *  100 years    570 ms   8 settlements, 163 records      still nothing travels
 *  120 years    440 ms  10 settlements, 278 records      the first wars and shocks
 *  150 years  1,000 ms  13 settlements, 423 records
 *  200 years  2,000 ms  16 settlements, 721 records      every channel, with margin
 *  300 years  3,600 ms  16 settlements
 * ```
 *
 * The cost grows faster than the years because the world does: more
 * settlements, more living figures, more relations to walk on each entity turn.
 *
 * Two hundred, for three reasons. It is where the phase's channels exist *with
 * margin* rather than barely — five rumours at two hops or more against two at
 * 150, six settlements whose prices have moved against four, and enough
 * repeated sackings that burned farmsteads stand on more than one road. It is
 * `sim-core`'s own two-century checkpoint and `sim-cli legends`' reference
 * world, so a playtest and a failing test are about the same place. And the two
 * seconds land once, on a page that is already fetching and compiling 630 KiB of
 * wasm.
 *
 * It is one constant and a URL parameter away from being re-judged, which is the
 * point: `?bootstrap=150` halves the wait, and wave 5B's seed scout may well
 * pick its own number along with its seed.
 *
 * `?bootstrap=0` is the Phase 1 world — no history, nobody to meet, and the dig
 * prototype exactly as it was.
 */
export const DEFAULT_BOOTSTRAP_YEARS = 200;

/** The URL parameter that overrides it: `?bootstrap=50`. */
const BOOTSTRAP_PARAM = "bootstrap";

/**
 * The most years the URL parameter will accept: 2,000.
 *
 * A guard rather than a limit of the simulation's: at the measured rate that is
 * a minute or so of construction with the page apparently hung, and a mistyped
 * `?bootstrap=200000` should say so rather than look like a crash. Anything at
 * this end of the range is a research run and belongs in `sim-cli legends`.
 */
const MAX_BOOTSTRAP_YEARS = 2_000;

/**
 * How far around the visited settlement the client pulls aftermath, in tiles.
 *
 * PROVISIONAL, and measured rather than guessed. Each of the three kinds is
 * placed at its own distance from the settlement it is about (`sim_core`'s
 * `aftermath` module): a grave two dozen tiles out, a battlefield marker an
 * eighth of the way towards the army that came, a burned farmstead a **quarter
 * of the way down the road out** — and a road in the reference world runs six
 * hundred tiles, so that last one is the number that sets this one. Counted
 * around every settlement of the two-century reference world:
 *
 * ```text
 *   128 tiles   graves and markers only; not one farmstead anywhere
 *   192 tiles   all three kinds; 14–23 props for a typical town, 77 for the
 *               one that was sacked five times
 *   256 tiles   the same, plus the first of the next town's graves
 * ```
 *
 * So 192: the first radius at which decision 6's three kinds are all reachable,
 * and the last one before a settlement starts drawing its neighbour's dead. The
 * far end of it is well past the fog — the view distance is
 * {@link INTEREST_RADIUS_CHUNKS} + 1 chunks, 80 tiles — which is the point: the
 * props are there when the player walks out along the road, and the list is
 * pulled once rather than followed.
 */
const FEATURE_RADIUS_TILES = 192;

/**
 * How long a group of refugees is still counted as being on the road, in days.
 *
 * PROVISIONAL, and a *presentation* window rather than a fact about travel: the
 * simulation records that a group fled and where the road led, and never where
 * anybody is standing (plan decision 8), so how long they are drawn walking is
 * the client's to decide. Fifteen days is the number that agrees with the walk
 * the client already draws — `witness/witness.ts` advances a group by 6% of its
 * route per day since it left and stops it at 88%, which is a fortnight — so a
 * group leaves the list at about the moment it would have arrived.
 *
 * The two numbers live in different files because they answer to different
 * layers, and this is the seam: if wave 5A retunes the walk, this moves with it.
 */
const REFUGEE_WINDOW_DAYS = 15;

/**
 * How long a caravan is still counted as being on the road, in days.
 *
 * PROVISIONAL, and {@link REFUGEE_WINDOW_DAYS}'s counterpart for Phase 4
 * decision 2: the simulation computes a turn's shipments and says where each
 * went and on what day, never where anybody is standing (Phase 3 decision 8), so
 * how long a column is drawn walking is the client's.
 *
 * Twenty-eight days, and it is a *ceiling on staleness* rather than a walk
 * length. The walk itself is ten days — `witness/witness.ts` advances a caravan
 * by a tenth of its route per day and draws it up short of the gate after that —
 * and the sim's own trade turn is a month, so it replaces the whole list before
 * this window can expire. What the number does is guarantee the road empties if
 * that cadence ever changes: a column from a turn a season old would otherwise
 * stand at the gate forever.
 *
 * The two numbers live in different files because they answer to different
 * layers, and this is the seam — the same one the refugee window has.
 */
const CARAVAN_WINDOW_DAYS = 28;

/**
 * Returns the facing that turns a monument's face out of a lot's door.
 *
 * A prop's geometry is built facing local `+y` and a monument's *front* is local
 * `-y` (`render/props.ts`), so a facing `f` puts the front at
 * `(sin f, −cos f)` in world space. This solves that for the outward step from
 * the work tile to the doorstep, which is axis-aligned by construction
 * (`sim_core::townplan`'s `doorway`) — and it degrades to the old fixed zero for
 * a lot whose two columns somehow coincide, which is a facing rather than a
 * crash.
 */
function facingOutward(lot: TownLot): number {
  const dx = lot.doorstep.x - lot.work.x;
  const dy = lot.doorstep.y - lot.work.y;

  return dx === 0 && dy === 0 ? 0 : Math.atan2(dx, -dy);
}

/**
 * Which building a market board belongs outside, in the simulation's own
 * selector vocabulary.
 *
 * `LotKind::name`'s table — *"the kind's name, in the vocabulary a debug view
 * selects by"* — asked for through {@link WorldSource.lotName} rather than kept
 * as a copy of an id, which is the standing rule about numbers with two homes
 * (`crates/sim-wasm/src/lib.rs`). A build whose simulation renamed the kind puts
 * the board back on {@link MARKET_BOARD_OFFSET} and says nothing, which is the
 * same graceful nothing every other vocabulary read here degrades to.
 */
const MARKET_BOARD_LOT = "market";

/**
 * Where the market board stands when the town has no market to stand it at, as
 * an offset from the settlement's centre.
 *
 * **A fallback since wave 1.5, and it used to be the whole rule.** A board is
 * not aftermath — nothing in the log put it there — so the simulation has no
 * opinion about where the *prop* is; but it has a very definite one about where
 * the market is, and the wave-1 playtest found the board in the middle of a
 * carriageway because this guess never asked
 * ({@link WorldSource.lots}). The guess is kept for the town that has no market
 * lot standing yet — a hamlet whose plan has not reached one — because a board
 * near the middle is better than no board at all, and the market read that puts
 * a board there in the first place is a fact about the town's prices rather than
 * about its buildings.
 *
 * Cosmetic and PROVISIONAL.
 */
const MARKET_BOARD_OFFSET = { x: 3, y: 5 };

/*
 * The strides of the witnessing layouts, transcribed from
 * `crates/sim-wasm/src/flatten.rs`.
 *
 * Every witnessing list crosses as one flat typed array with no header, read by
 * index (plan decision 11). The layouts are stated once, in that module's
 * documentation, and these are the transcription — named rather than inlined
 * because `i + 4` in six loops is six chances to write `i + 3`.
 *
 * Two element types and one sentinel:
 *
 * - `Int32Array` where nothing carries a record id — settlements, routes, the
 *   roster;
 * - `Float64Array` where something does, because an `EventId` is a `u64` counter
 *   and a `number` is exact to 2^53 while an `i32` is not;
 * - {@link NONE} where `sim-core` said `None`, which is `-1` and cannot collide
 *   because every value it stands in for is a non-negative id or date.
 */
const SETTLEMENT_STRIDE = 3;
const ROUTE_STRIDE = 2;
const ROSTER_STRIDE = 5;
const RUMOR_STRIDE = 5;
const MARKET_STRIDE = 8;
const FEATURE_STRIDE = 5;
const FLIGHT_STRIDE = 6;
const PULSE_STRIDE = 6;
const CHRONICLE_STRIDE = 9;
const RUIN_STRIDE = 4;
const SAGA_ERA_STRIDE = 7;
const ORIGIN_STRIDE = 8;
const CLAIM_STRIDE = 4;
const JOB_STRIDE = 5;
const PRESENCE_STRIDE = 6;
const ITEM_STRIDE = 4;
const LOT_STRIDE = 6;
const SHELF_STRIDE = 2;
const OPEN_GROUND_STRIDE = 3;

/**
 * The beat layout's stride, and how many record slots each row ends with.
 *
 * Five columns — id, surface, subject, year, day — and then `Beat::MAX_CITED`
 * slots holding the records the beat is about, {@link NONE} where a beat cites
 * fewer than the reservation allows. A fixed stride rather than a count column,
 * because the width is `sim-core`'s own published constant and a dense array
 * read by index is the whole shape of this boundary
 * (`crates/sim-wasm/src/flatten.rs`).
 */
const BEAT_STRIDE = 7;
const BEAT_CITED = 2;

/**
 * The saga's citation layout, which is the one list on this boundary with a
 * **variable** stride: `slot, cites, ...records` per line, in reading order.
 *
 * Two leading columns and then as many record ids as the count named. It takes
 * this shape rather than a beat's fixed one because the width is not a constant
 * `sim-core` publishes — a fixed stride would be built on a number both sides
 * transcribed, and would silently drop a third citation the day one existed,
 * which is the one thing a *trace* contract must not do
 * (`crates/sim-wasm/src/flatten.rs` argues it in full).
 *
 * So the array is walked by its own counts, and {@link MAX_SAGA_LINE_CITES} is a
 * bound this client checks at startup rather than a width it decodes with.
 */
const SAGA_CITE_HEADER = 2;

/** What a flat array says where `sim-core` said `None` (`crates/sim-wasm/src/flatten.rs`). */
const NONE = -1;

/** A full turn, for the prop facings derived below. */
const TAU = Math.PI * 2;

/**
 * The golden angle, in radians.
 *
 * The same constant `witness/witness.ts` spreads a crowd with, and here for the
 * same property: consecutive multiples of it never line up, so consecutive
 * records do not produce a row of props facing the same way.
 */
const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));

/**
 * Chunks meshed per drain of {@link WasmWorldSource.takeMeshUpdates}.
 *
 * Sixteen, against a measured 0.05–0.07 ms per slab including the buffer copy:
 * about a millisecond of a 16 ms frame. The registry uploads at most eight of
 * them (`render/chunkMeshes.ts`), which is the number the research brief caps
 * geometry creation at (§2, "2–4 per frame" — eight because half our slabs mesh
 * to nothing and never reach an upload). Meshing runs ahead of uploading on
 * purpose: the empty answers are the cheap ones and finding them is what turns a
 * window of 1,295 slabs into 739 meshes.
 */
const MESH_BUDGET_PER_DRAIN = 16;

/** A chunk this client has meshed, and whether that mesh had any triangles. */
interface MeshedChunk {
  readonly coord: ChunkCoord;
  readonly hasGeometry: boolean;
}

/** What {@link createWasmWorld} hands back: the world, and where to stand in it. */
export interface WasmWorld {
  /** The simulation, behind the seam. */
  readonly world: WorldSource;

  /**
   * What the world says about itself, behind the same seam.
   *
   * The live implementation of {@link WitnessSource} — the same interface
   * `world/fixture.ts` hand-authored for waves 1B and 2B, over the real history
   * tier. `null` when the session asked for no bootstrap (`?bootstrap=0`): a
   * world where nothing has happened has no settlements, nobody to meet and
   * nothing to say, and a source that answered every question with an empty
   * list would put an interact prompt on an empty field.
   */
  readonly witness: WitnessSource | null;

  /**
   * Where to put the player's feet — see {@link createWasmWorld} — and whose
   * town that is, or `null` in a world with no towns.
   *
   * **The settlement is carried because of one instant.** A name is claimed in
   * the boot sequence, before the world's first tick, and a claim needs a town
   * to seat the figure in — but `here()` is the promotion slot and a settlement
   * promotes *inside* that first tick, so at the moment of the claim there is no
   * promoted town to name (item 6's rider 4). The boot sequence already chose
   * one to stand the player in, and this is that choice made readable rather
   * than a second answer to the same question (`witness/witness.ts`'s
   * `WitnessOptions.home`).
   */
  readonly spawn: {
    readonly x: number;
    readonly y: number;
    readonly z: number;
    readonly settlement: SettlementId | null;
  };

  /** How far the world can be seen, in tiles: what the fog and the far plane size from. */
  readonly viewDistance: number;

  /**
   * How many ticks the simulation spends walking a body across one tile
   * (`sim_wasm::walk_ticks_per_step`).
   *
   * Forwarded rather than copied, unlike the caps this file checks a client-side
   * constant against — because nothing here *lays anything out* against it, so
   * there is no reason for a second copy to exist at all. What reads it is the
   * walker smoothing (`witness/scenes.ts`), which is presentation over two
   * positions the simulation has already stated.
   */
  readonly walkTicksPerStep: number;
}

/**
 * How many recorded ticks a restore re-runs between two paints.
 *
 * **A budget, measured rather than guessed**: a tick costs 0.19 ms in the
 * release build (the table above), so this is about twenty-five milliseconds of
 * work per turn — enough that a long file finishes in the wall time the ticks
 * themselves cost, and short enough that the count on the loading line moves
 * several times a second. Nothing is drawn during a restore, so the only thing
 * this trades against is how often that number changes.
 */
const RESTORE_TICKS_PER_TURN = 128;

/** How far through a file a restore is, for the line the client draws over it. */
export interface RestoreProgress {
  /** How many of the file's ticks have been re-run. */
  readonly done: number;

  /** How many it holds. */
  readonly total: number;
}

/** What a restored session cost. */
export interface RestoredSession {
  /**
   * How many recorded ticks were re-run.
   *
   * Every one of them was checked against the hash the file says it produced,
   * so this is also the claim that the load worked: a re-run that produced
   * anything else was refused at the tick it happened
   * (`WasmSim::advance_replay`).
   */
  readonly ticks: number;

  /** What the whole load cost in wall seconds, bootstrap included. */
  readonly seconds: number;
}

/** A world that came out of a file, and how it got here. */
export interface RestoredWorld extends WasmWorld {
  readonly restored: RestoredSession;
}

/**
 * Either the session, or the sentence to show whoever chose the file.
 *
 * Two shapes rather than a throw, because a refused file is an ordinary answer:
 * the player chose it, the screen says what is wrong with it, and the rail is
 * still up behind the sentence ({@link restoreWasmWorld}).
 */
export type RestoreOutcome = RestoredWorld | { readonly refused: string };

/**
 * Loads the simulation and starts a world.
 *
 * Everything asynchronous about the client happens here and nowhere else:
 * instantiating the wasm module. The raws travel with the bundle rather than
 * being fetched (`?raw`), because they are an *input to the simulation* — the
 * material fingerprint is recorded in every replay — and a file that can be
 * stale independently of the module that reads it is a divergence waiting to
 * happen (`scripts/sync-wasm.mjs`).
 *
 * The spawn point is the surface under the town, asked of the sim *before* any
 * terrain exists: `ground_level` re-derives the elevation field from
 * `(seed, x, y)` instead of reading the map, which is exactly what a client
 * needs to place a player before the first tick has generated anything
 * (`WasmSim::ground_level`).
 *
 * **The player spawns in a settlement, not at the origin.** Phase 3's exit
 * criterion begins "stand in town A" (roadmap), and the towns are where the seed
 * put them — the nearest is up to fifteen hundred tiles from the origin
 * (`sim_core::history::sites`' candidate grid). Walking there is not a mechanic,
 * so the session starts there. A world with no history has no towns and starts
 * at the origin, exactly as every session before this phase did.
 */
export async function createWasmWorld(): Promise<WasmWorld> {
  await loadTheSimulation();
  checkConstants();

  return worldAround(new WasmSim(seedFromLocation(), rawsToml, bootstrapFromLocation()), null);
}

/**
 * Fetches and compiles the wasm module, once, and hands back the same promise
 * to everybody who asks.
 *
 * **The one piece of the world a session may load before the player has asked
 * for one** (chunk T): the module is 630 KiB of bytes over the network and
 * decides nothing — no world is constructed, no seed is read, nothing is
 * generated — so it may be on its way while the title screen is up, and *begin*
 * then pays for the bootstrap alone. Building a world is the other half and it
 * waits for the press ({@link createWasmWorld}).
 *
 * One promise rather than a call each: `init()` is idempotent once it has
 * resolved, but two calls that overlap fetch and compile the module twice, and a
 * player who presses *begin* while the prefetch is still in the air is exactly
 * the overlap.
 */
export function loadTheSimulation(): Promise<unknown> {
  loading ??= init();

  return loading;
}

/** The module load, once it has been asked for. See {@link loadTheSimulation}. */
let loading: Promise<unknown> | null = null;

/**
 * Restores a session from the text of a replay file.
 *
 * **A save is a replay, so a load is a re-run** (`crates/sim-wasm`'s `record`,
 * decision D-5): the world is rebuilt from the seed, the raws and the bootstrap
 * years the file names, and every command it recorded is submitted again to the
 * tick it was submitted to, with every tick's hash checked against the one the
 * recording says it produced. There is no snapshot of a world anywhere in this
 * tree to load instead.
 *
 * The re-run is **budgeted**, which is the whole reason it is a loop here rather
 * than one call: `onProgress` is awaited between batches, so the caller paints
 * the wait and counts the ticks — a page that ran a hundred thousand ticks
 * inside one call would be a page that is not answering.
 *
 * Refusals come back **as a value**, not as a throw: a file that is not one of
 * ours is something the player chose and can choose again, so the screen that
 * offered the door shows the sentence and stays up (`session/restore.ts`).
 */
export async function restoreWasmWorld(
  replayJson: string,
  onProgress: (progress: RestoreProgress) => Promise<void> | void,
): Promise<RestoreOutcome> {
  await loadTheSimulation();
  checkConstants();

  const started = performance.now();
  let sim: WasmSim;

  try {
    sim = WasmSim.restore_from_replay(replayJson, rawsToml);
  } catch (error) {
    return { refused: refusalOf(error) };
  }

  // Nothing is run: it is how the boundary answers "how long is this file",
  // which is the number the wait counts towards (`WasmSim::advance_replay`).
  let remaining = sim.advance_replay(0);
  const total = remaining;

  while (remaining > 0) {
    await onProgress({ done: total - remaining, total });

    try {
      remaining = sim.advance_replay(RESTORE_TICKS_PER_TURN);
    } catch (error) {
      // The world is past the tick that diverged and is not one anybody
      // recorded, so the handle goes rather than being handed back.
      sim.free();

      return { refused: refusalOf(error) };
    }
  }

  await onProgress({ done: total, total });

  const stood = sim.restored_position();
  const at =
    stood.length === 3
      ? { x: stood[0] ?? 0, y: stood[1] ?? 0, z: stood[2] ?? 0 }
      : null;

  return {
    ...worldAround(sim, at),
    restored: { ticks: total, seconds: (performance.now() - started) / 1000 },
  };
}

/**
 * The world, the witnessing over it and where to stand in it, for both doors.
 *
 * `stood` is where a restored file said the player was, or `null` for a world
 * that has just been built — which starts in the settlement nearest the origin,
 * exactly as every session since Phase 3 has ({@link createWasmWorld}).
 */
function worldAround(sim: WasmSim, stood: TilePos | null): WasmWorld {
  const world = new WasmWorldSource(sim);
  const home = nearestSettlement(sim, stood?.x ?? 0, stood?.y ?? 0);
  const x = stood?.x ?? home?.x ?? 0;
  const y = stood?.y ?? home?.y ?? 0;
  const ground = sim.ground_level(x, y);

  return {
    world,
    // No town, no witnessing: see `WasmWorld.witness`.
    witness: home === null ? null : new WasmWitnessSource(sim, world),
    // Tile centre, and one level above the walkable surface: the player's feet
    // go in the open tile above the floor, not inside it.
    spawn: {
      x: x + 0.5,
      y: y + 0.5,
      z: ground + 1,
      settlement: home?.settlement ?? null,
    },
    // One chunk past the interest square, so the fog saturates before the ring's
    // edge does rather than at it.
    viewDistance: (INTEREST_RADIUS_CHUNKS + 1) * CHUNK_SIZE,
    walkTicksPerStep: walk_ticks_per_step(),
  };
}

/** What a refused load says, which is the simulation's own sentence about the file. */
function refusalOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/** The {@link WorldSource} over `sim-wasm`. Owns the sim handle and the mesh bookkeeping. */
export class WasmWorldSource implements WorldSource {
  readonly terrain: TerrainSampler;

  private readonly sim: WasmSim;
  private readonly window: MeshWindow;

  /** The interest the client last asked for, applied on the next {@link step}. */
  private requestedInterest: { center: TilePos; radiusChunks: number } | null = null;

  /** The interest already submitted as a command, so a repeat costs nothing. */
  private submittedInterest: { cx: number; cy: number; radiusChunks: number } | null = null;

  /**
   * The tile already submitted as a stand, so a repeat costs nothing.
   *
   * {@link submittedInterest}'s sibling one command over, and deliberately
   * *not* a `requested`/`submitted` pair like it: interest is offered every
   * tick and sent when it changes, and a stand is offered only when it matters
   * and has to be sent **now** — see {@link WasmWorldSource.stand}.
   */
  private submittedStand: TilePos | null = null;

  /** Chunks that have been meshed, by chunk key. */
  private readonly meshed = new Map<string, MeshedChunk>();

  /** Chunks waiting to be meshed, nearest first, and the same set for deduplication. */
  private pending: ChunkCoord[] = [];
  private readonly pendingKeys = new Set<string>();

  /** Mesh changes since the last drain. */
  private updates: ChunkMeshUpdate[] = [];

  /**
   * Tiles the ticks since the last drain opened ({@link takeDugTiles}).
   *
   * Accumulated rather than read per query, because the event stream belongs to
   * a `TickSummary` that is freed at the end of the tick that produced it: the
   * three positions are copied out here and the wasm-bindgen object goes, which
   * is this file's rule for every per-tick object it touches.
   */
  private dug: TilePos[] = [];

  /**
   * The world's clock, as of the last tick — read off the simulation at
   * construction rather than started at zero.
   *
   * It is the *world's* rather than the session's (`WasmSim::tick_count`), so a
   * world with a bootstrap behind it starts at the end of those years and a
   * restored one starts at the tick its file ended on. Starting it at zero was
   * true for exactly one frame and false about both: the readout showed a
   * two-century world as tick 0 until the first step, and a load had no way to
   * say where it landed (`main.ts` writes the tick and the hash out for a
   * driver before the loop moves either).
   */
  private tick: number;
  private worldHash: string;
  private generatedChunks = 0;
  private dugTiles = 0;

  /**
   * Today's date, and how many times the calendar has turned over.
   *
   * Kept from the tick summary rather than asked of the simulation, because it
   * arrives with every tick for free and asking would be a call per frame for a
   * number that changes **once a day** — and a day is tens of thousands of ticks
   * (`sim_core::calendar`, `TickSummary::day_changed`). How many exactly is the
   * simulation's business and this client has never written it down: the one
   * sub-day question it asks is {@link WasmWitnessSource.timeOfDay}, which is a
   * seam rather than arithmetic.
   */
  private date: WorldDateView;
  private days = 0;

  /**
   * How many records the world's history holds, and therefore whether anything
   * anybody could witness has happened since the last pull.
   *
   * **This is the cadence** (plan decision 11): the witnessing surface is slow
   * bulk, and this is the stamp its caches hang on. A tick that appends nothing
   * — which is almost all of them, the reference world logs about four records a
   * year — leaves every cached list valid.
   */
  private records: number;

  constructor(sim: WasmSim) {
    this.sim = sim;
    this.tick = sim.tick_count();
    this.worldHash = sim.world_hash_hex();
    this.date = { year: sim.date_year(), dayOfYear: sim.date_day_of_year() };
    this.records = sim.history_count();
    this.window = new MeshWindow(
      INTEREST_RADIUS_CHUNKS,
      { min: world_z_min(), max: world_z_max() },
      (columns) => this.columnSpans(columns),
    );

    // `is_solid` first: it is `false` for a tile no chunk holds, so the second
    // question is only asked about tiles that did not already answer. Collision
    // asks mostly about solid ground, which makes the common case one call.
    //
    // The two shape queries pass straight through. They need no client-side
    // policy because an ungenerated tile already answers them the safe way — no
    // terrain, no slope — and the unknown-is-solid rule above is what actually
    // holds a player up at the streaming frontier.
    this.terrain = {
      isSolid: (x: number, y: number, z: number): boolean =>
        sim.is_solid(x, y, z) || !sim.is_generated(x, y, z),
      // The same question without the client's safe reading on top, for callers
      // that have to tell "rock" from "not here yet" (`world/types.ts`).
      isGenerated: (x: number, y: number, z: number): boolean => sim.is_generated(x, y, z),
      // And what a solid tile *is*, for a scan looking for the ground: a trunk
      // and a house wall are both things standing on the ground rather than the
      // ground (`world/types.ts`).
      standsOnGround: (x: number, y: number, z: number): boolean =>
        sim.stands_on_ground(x, y, z),
      tileShape: (x: number, y: number, z: number): number => sim.tile_shape(x, y, z),
      rampRise: (x: number, y: number, z: number): number => sim.ramp_rise_direction(x, y, z),
      // Straight through, and deliberately map-free: it re-derives the
      // elevation field from the seed, so it answers for columns no interest
      // has generated. That is what makes it the right question to ask when
      // placing something before the ground under it exists.
      groundLevel: (x: number, y: number): number => sim.ground_level(x, y),
    };
  }

  setInterest(center: TilePos, radiusChunks: number): void {
    this.requestedInterest = { center, radiusChunks };
  }

  dig(pos: TilePos): void {
    // Straight into the sim's queue, which is where a command waits for the
    // next tick. Nothing here checks whether the tile can be dug: that is the
    // simulation's decision, and it reports what it did as an event
    // (`WasmSim::queue_dig`).
    this.sim.queue_dig(pos.x, pos.y, pos.z);
  }

  /*
   * The player's deeds, and the shortest methods in this file.
   *
   * Every one of them is the `dig` pattern exactly — hand it over, decide
   * nothing, read the world afterwards to find out what became of it. That is
   * not brevity for its own sake: a claim that checked whether the name was
   * empty, a telling that checked whether the town already knew, or a book that
   * trimmed itself to the cap would each be a rule about the world living in
   * JavaScript, and each of them is a rule the replay format also has to agree
   * with (`world/types.ts`).
   */

  /*
   * The birth door: three methods, and each is a forward of the export named
   * after it.
   *
   * Nothing is decided here — not which door a card belongs to (its `kind` is),
   * not whether the life is still legal (the simulation re-checks every rule
   * behind the screen), and not whether the name is anything (the wall
   * normalizes it). The one thing this side must not do is turn a card into
   * anything other than the handles it arrived with, because those handles are
   * what makes the recorded birth mean the same life on re-run
   * (`world/types.ts`).
   */

  queueBornOrphan(name: string, settlement: SettlementId, coinNonce?: number): void {
    this.sim.queue_born_orphan(name, settlement, coinNonce);
  }

  queueBornUnrecorded(
    name: string,
    mother: FigureId,
    father: FigureId,
    coinNonce?: number,
  ): void {
    this.sim.queue_born_unrecorded(name, mother, father, coinNonce);
  }

  queueBornHeir(name: string, figure: FigureId, coinNonce?: number): void {
    this.sim.queue_born_heir(name, figure, coinNonce);
  }

  queueBornBound(name: string, figure: FigureId, coinNonce?: number): void {
    this.sim.queue_born_bound(name, figure, coinNonce);
  }

  /*
   * The coined-name previews: three reads that take the same arguments the three
   * doors above take, and change nothing.
   *
   * They are on `WorldSource` rather than on the witnessing surface because they
   * belong to the *door* — the arguments are a birth's, the answer is about a
   * command, and the screen that asks them is the one holding the card
   * (`hud/origins.ts`). What separates them from the three above is one word in
   * their documentation and it is the load-bearing one: nothing is queued.
   */

  coinedOrphanHouse(name: string, settlement: SettlementId, coinNonce: number): string {
    return this.sim.born_orphan_house_name(name, settlement, coinNonce);
  }

  coinedUnrecordedHouse(
    name: string,
    mother: FigureId,
    father: FigureId,
    coinNonce: number,
  ): string {
    return this.sim.born_unrecorded_house_name(name, mother, father, coinNonce);
  }

  coinedBoundHouse(name: string, figure: FigureId, coinNonce: number): string {
    return this.sim.born_bound_house_name(name, figure, coinNonce);
  }

  claim(name: string, settlement: SettlementId): void {
    // The typed string, raw. The wall that trims, collapses and cuts it is
    // `sim_core::history::PlayerName`, and it is the same wall a hand-edited
    // replay goes through — which is what makes a recorded name reproduce the
    // world it was recorded in (`crates/sim-wasm`'s `queue_claim`).
    this.sim.queue_claim(name, settlement);
  }

  tell(settlement: SettlementId, event: EventId): void {
    this.sim.queue_tell(settlement, event);
  }

  /*
   * The player's hands, and they are the `dig` pattern four more times.
   *
   * Nothing here checks a reach, a legality or a cap. Every one of those is a
   * rule about the world, every one of them is enforced sim-side whatever this
   * file believes, and a copy here would be the second opinion the replay format
   * cannot afford (`world/types.ts`). What the *client* owns is where the player
   * is standing, which is why the reach rules live above this seam and the
   * command carries only a tile.
   */

  gather(pos: TilePos): void {
    this.sim.queue_gather(pos.x, pos.y, pos.z);
  }

  craft(pos: TilePos, recipe: number | null): void {
    // `undefined` rather than `null` for the demand rule: wasm-bindgen maps a
    // JavaScript `undefined` onto the `Option<u32>`'s `None`, which is the
    // boundary's own spelling of *"whatever this bench wants"*
    // (`crates/sim-wasm`'s `queue_craft`).
    this.sim.queue_craft(pos.x, pos.y, pos.z, recipe ?? undefined);
  }

  take(pos: TilePos): void {
    this.sim.queue_take(pos.x, pos.y, pos.z);
  }

  drop(pos: TilePos): void {
    this.sim.queue_drop(pos.x, pos.y, pos.z);
  }

  /**
   * Tells the world which tile the player is standing on, when it is news.
   *
   * **Deduped like {@link WasmWorldSource.setInterest} and for the same contract**
   * (Phase 7 plan, W11): the simulation *holds* what it was told until the next
   * one supersedes it, so a repeat of the tile it already has could not change
   * the fold even if it were sent. What the dedupe buys is the replay — a
   * command log with a tile on every tick of a fight is a file nobody wants to
   * read, and *"every fight and nothing else"* is the bargain the command was
   * designed under.
   *
   * Unlike interest, it is submitted **immediately** rather than at the next
   * {@link WasmWorldSource.step}: the caller that sends it before a strike needs both
   * in the same tick's batch, in that order, and a deferred write would put the
   * tile in the batch after the swing it was for.
   */
  stand(pos: TilePos): void {
    const held = this.submittedStand;

    if (held !== null && held.x === pos.x && held.y === pos.y && held.z === pos.z) {
      return;
    }

    this.submittedStand = { x: pos.x, y: pos.y, z: pos.z };
    this.sim.queue_stand(pos.x, pos.y, pos.z);
  }

  strike(figure: FigureId): void {
    // Straight into the queue, like {@link WasmWorldSource.dig}: whether the swing is
    // in reach, off the cooldown or from a hand that can hold anything is the
    // simulation's decision, and it reports what it did by what the world looks
    // like afterwards (`WasmSim::queue_strike`).
    this.sim.queue_strike(figure);
  }

  loose(figure: FigureId): void {
    // The strike's twin, and queued exactly as bluntly: which bow, which arrow,
    // whether it is in range and what it did to whoever it hit are every one of
    // them the simulation's (`WasmSim::queue_loose`).
    this.sim.queue_loose(figure);
  }

  wear(item: number | null = null): void {
    // `undefined` rather than `null` across the wall, for the same reason
    // {@link WasmWorldSource.craft} spells its recipe that way: wasm-bindgen maps
    // a JavaScript `undefined` onto the `Option<u32>`'s `None`, which is the
    // boundary's own spelling of *"whatever the world would have picked"* — the
    // most protective carried piece whose part is still bare
    // (`WasmSim::queue_wear`).
    //
    // The default is the nameless form because that is what every call site in
    // this client has ever meant, and because the replay writes it as the bare
    // word `"wear"` that every recorded file already carries (chunk S-2).
    this.sim.queue_wear(item ?? undefined);
  }

  wield(item: number): void {
    // The equip choice, queued as bluntly as the swing: whether that number is
    // still in the player's hands is resolved sim-side against those hands and
    // nowhere else, so a handle this client read a frame ago names one of eight
    // things or nothing at all (`WasmSim::queue_wield`).
    this.sim.queue_wield(item);
  }

  doff(): void {
    this.sim.queue_doff();
  }

  beckon(figure: FigureId): void {
    // Straight into the queue like the swing, and for its reason: whether the
    // offer stands, whether the hero is near enough and whether the person is
    // free to go are every one of them the simulation's
    // (`WasmSim::queue_beckon`). The tile goes first — the caller pairs them,
    // exactly as it does for a strike.
    this.sim.queue_beckon(figure);
  }

  dismiss(figure: FigureId): void {
    this.sim.queue_dismiss(figure);
  }

  /*
   * The developer's door (chunk Fx-3, `dev/give.ts`).
   *
   * **Four members that are not on `WorldSource`, on purpose.** They are read
   * through a structural interface declared in `dev/` — `GiveDoor`, which is
   * `dev/debuglog.ts`'s `LocalWatch` pattern — so the production surface every
   * stub and the hand-authored fixture implement stays exactly as wide as it
   * was. What gates the door is the *caller*: `main.ts` builds the tab that
   * uses these only in a `?dev=1` session, and the argument for why the gate is
   * client-side and the command is not is in `sim_core::conjure`.
   */

  /** Every kind-and-shape pair the simulation ships, with the word for each. */
  itemNouns(): readonly { kind: number; shape: number; noun: string }[] {
    const rows: { kind: number; shape: number; noun: string }[] = [];

    // Two nested table walks and no client-side list: the kinds are DF's
    // `item_type` numbering with its gaps, and the shapes are per kind — a
    // weapon comes in two and everything else in one (`sim_core::ItemKind`).
    for (const kind of item_kind_ids()) {
      for (const shape of item_shape_ids(kind)) {
        rows.push({ kind, shape, noun: item_noun(kind, shape) });
      }
    }

    return rows;
  }

  /** Every material this world's raws name, in id order — the index is the id. */
  materialNames(): readonly string[] {
    return this.sim.material_names();
  }

  /** Whether a material is a solid, by its id (`WasmSim::material_solid`). */
  materialSolid(material: number): boolean {
    return this.sim.material_solid(material);
  }

  /** Queues one conjure: an item of a kind, shape and material, on a tile. */
  conjure(pos: TilePos, kind: number, shape: number, material: number): void {
    // Straight into the queue like every other command, and refused in silence
    // for every reason the simulation refuses one (`WasmSim::queue_conjure`).
    this.sim.queue_conjure(pos.x, pos.y, pos.z, kind, shape, material);
    // **And the tile joins the ones worth looking at** ({@link takeDugTiles}).
    // Found by building the tab: there is deliberately no *"every pile in the
    // world"* iterator, so the witnessing layer draws a heap only on a tile
    // something told it to watch — dug tiles, work sites, benches — and a blade
    // that appeared under the player's feet on a tile nobody had a reason to
    // know about would be a blade that is genuinely there and genuinely
    // invisible. A conjure changed that tile, which is the same sentence a dig
    // makes, so it goes down the same seam.
    //
    // It is pushed **optimistically**, before the tick that may refuse the
    // command: a watched tile with nothing on it is dropped on the next read
    // (`witness/witness.ts`'s `repile`), so the cost of being wrong is one tile
    // lookup and the cost of waiting for certainty would be a second channel
    // out of the tick that nothing else needs.
    this.dug.push({ x: pos.x, y: pos.y, z: pos.z });
  }

  placeChronicle(entries: readonly EventId[], pos: TilePos): void {
    // A fresh `Float64Array` per call, which is what the boundary takes: record
    // ids cross as `f64` everywhere on this surface, so the ids handed back are
    // the ids that were handed over. The cap is the simulation's and is not
    // applied here — see `world/types.ts`.
    this.sim.queue_place_chronicle(Float64Array.from(entries), pos.x, pos.y, pos.z);
  }

  step(): void {
    this.submitInterest();

    // Before the tick, not after: the tick's dirty list is filtered against the
    // window, so the window has to already know where the player is. On the
    // first tick the difference is thousands of chunks — interest generates the
    // whole square and reports every one of them.
    this.updateWindow();

    const summary = this.sim.tick();

    this.tick = summary.tick;
    this.worldHash = summary.world_hash_hex;
    this.generatedChunks = summary.chunk_count;

    // The witnessing tier's clock, read off the summary and nowhere else. Two
    // scalars on an object the client already holds, against a query per frame
    // for a number that moves once a day (plan decision 11).
    if (summary.day_changed) {
      this.date = { year: summary.date_year, dayOfYear: summary.date_day_of_year };
      this.days += 1;
    }

    // Separately from the day, and not inside it: history only appends on a day
    // boundary today (`sim_core::history`'s World stage), and a client that
    // assumed so would be a client that stopped noticing the day it stopped
    // being true.
    if (summary.records_added > 0) {
      this.records = summary.history_count;
    }

    if (summary.dirty_chunk_count > 0) {
      this.queueDirty(summary.dirty_chunks());
    }

    if (summary.dug_count > 0) {
      this.queueDug(summary.dug_events());
    }

    // Freed here rather than left to the finalizer: one of these is created
    // every tick, and "eventually" is not a budget (`crates/sim-wasm`).
    summary.free();
  }

  takeMeshUpdates(): ChunkMeshUpdate[] {
    this.meshPending();

    const drained = this.updates;
    this.updates = [];

    return drained;
  }

  takeDugTiles(): readonly TilePos[] {
    const dug = this.dug;
    this.dug = [];

    return dug;
  }

  stats(): WorldStats {
    let loaded = 0;

    for (const chunk of this.meshed.values()) {
      if (chunk.hasGeometry) {
        loaded += 1;
      }
    }

    return {
      tick: this.tick,
      loadedChunks: loaded,
      generatedChunks: this.generatedChunks,
      dugTiles: this.dugTiles,
      worldHash: this.worldHash,
    };
  }

  exportReplay(): string {
    return this.sim.export_replay();
  }

  /**
   * Returns today's date, as of the last tick.
   *
   * Not part of {@link WorldSource} — it is the witnessing surface's question
   * ({@link WitnessSource.date}) — but it is *this* object that has the answer,
   * because the answer arrives on the tick summary. {@link WasmWitnessSource}
   * reads it from here rather than asking the simulation again.
   */
  today(): WorldDateView {
    return this.date;
  }

  /**
   * Returns a stamp that changes when anything witnessable might have.
   *
   * The two halves of "pull slow bulk only then": the day count moves when the
   * calendar does, and the record count moves when the world wrote something
   * down. A cache that compares this against what it was built with re-pulls on
   * the ticks that could have changed its answer and on no others.
   */
  stamp(): number {
    // Cantor-free and deliberately crude: the two numbers only ever increase, so
    // their sum is a stamp that changes whenever either does. It is compared for
    // equality and never read as a quantity.
    return this.days + this.records;
  }

  /**
   * Returns the tile the client last told the world the player was on.
   *
   * The player's position is client state and never crosses into the simulation
   * (plan decision 10), but the *interest* command carries it — so this is that
   * command's centre. `null` before the first {@link setInterest}, which is
   * before the client has a player.
   *
   * It is **not** how the visited settlement is resolved any more: that is the
   * simulation's promotion slot and nothing else ({@link WitnessSource.here},
   * item 6's rider 4). What still reads this is the debug window, which reports
   * where the client last said the player was.
   */
  playerTile(): TilePos | null {
    return this.requestedInterest?.center ?? null;
  }

  /**
   * Submits the interest command, but only when it says something new.
   *
   * **The dedupe is the contract rather than a saving** (item 6, ruled
   * 2026-08-28). It used to be a saving resting on a belief — *"the world
   * ignores a repeat"* — that was true of terrain and false of promotion: the
   * simulation counted the ticks since it last *heard* an interest and demoted
   * the town after 750 of them, so a player who stood still for twenty-five
   * seconds lost the tier under their feet. The fix was on the other side of
   * the wall and it makes this line correct instead of nearly correct:
   * `Command::Interest` now means *"coverage changed to this"*, the simulation
   * **holds** the set until told otherwise, and a repeat of what is already
   * held could not change the fold even if it were sent
   * (`world/types.ts`, and `sim_core::local`'s `follow_interest`).
   *
   * What it buys is still the replay: a command log with an entry on every tick
   * is an order of magnitude larger than one without, and a native host would
   * not want to send a 30 Hz command either.
   */
  private submitInterest(): void {
    const requested = this.requestedInterest;

    if (requested === null) {
      return;
    }

    const cx = floorDiv(requested.center.x, CHUNK_SIZE);
    const cy = floorDiv(requested.center.y, CHUNK_SIZE);
    const submitted = this.submittedInterest;

    if (
      submitted !== null &&
      submitted.cx === cx &&
      submitted.cy === cy &&
      submitted.radiusChunks === requested.radiusChunks
    ) {
      // Already held, and holding it is what the simulation does with it — see
      // the note above.
      return;
    }

    this.submittedInterest = { cx, cy, radiusChunks: requested.radiusChunks };
    this.sim.queue_interest(
      requested.center.x,
      requested.center.y,
      requested.center.z,
      requested.radiusChunks,
    );
  }

  /**
   * Queues the chunks a tick changed, as `cx, cy, z` triples.
   *
   * The flattened layout is `crates/sim-wasm/src/flatten.rs`'s and is read by
   * index: `3n` values, one triple per chunk, no header. Chunks outside the
   * window are dropped rather than remembered — when the window reaches them
   * they will be meshed from scratch anyway, and the first interest of a session
   * reports seven thousand of these.
   */
  private queueDirty(dirty: Int32Array): void {
    for (let i = 0; i + 2 < dirty.length; i += 3) {
      const coord = {
        cx: dirty[i] ?? 0,
        cy: dirty[i + 1] ?? 0,
        z: dirty[i + 2] ?? 0,
      };

      if (this.window.contains(coord)) {
        this.enqueue(coord, false);
      }
    }
  }

  /**
   * Queues the neighbourhood of every tile a tick dug, as `x, y, z` triples.
   *
   * Same flattened layout, tiles rather than chunks. The expansion is the
   * client's job and the reason is the mesher's padded input — see
   * {@link forEachChunkNear}. Straight to the front of the queue: a hole the
   * player just made must not wait behind a horizon's worth of first-load
   * meshing.
   */
  private queueDug(dug: Int32Array): void {
    for (let i = 0; i + 2 < dug.length; i += 3) {
      this.dugTiles += 1;

      const pos = { x: dug[i] ?? 0, y: dug[i + 1] ?? 0, z: dug[i + 2] ?? 0 };

      // Where a particle burst or a pick sound would be triggered from: the
      // event carries the tile and `TickSummary::dug_materials` carries what it
      // was made of. Phase 1's cut list has no sound in it; since Phase 7 the
      // tile itself is handed up ({@link takeDugTiles}), because a tile that was
      // just opened is a tile with spoil on it and the witnessing layer wants to
      // know which ones to look at.
      this.dug.push(pos);

      forEachChunkNear(pos, (coord) => {
        if (this.window.contains(coord)) {
          this.enqueue(coord, true);
        }
      });
    }
  }

  /** Re-centres the mesh window on the player, evicting and enqueueing if it moved. */
  private updateWindow(): void {
    const requested = this.requestedInterest;

    if (requested === null || !this.window.moveTo(requested.center, requested.radiusChunks)) {
      return;
    }

    for (const [key, chunk] of this.meshed) {
      if (this.window.contains(chunk.coord)) {
        continue;
      }

      this.meshed.delete(key);

      if (chunk.hasGeometry) {
        this.updates.push({ kind: "remove", coord: chunk.coord });
      }
    }

    // Chunks already queued survive the move if they are still wanted: dropping
    // and re-adding them would lose the re-mesh a dig asked for.
    const kept = this.pending.filter((coord) => {
      if (this.window.contains(coord)) {
        return true;
      }

      this.pendingKeys.delete(chunkKey(coord));

      return false;
    });

    this.window.forEach((coord) => {
      const key = chunkKey(coord);

      if (this.meshed.has(key) || this.pendingKeys.has(key)) {
        return;
      }

      this.pendingKeys.add(key);
      kept.push(coord);
    });

    // Nearest first, so the ground under the player exists before the horizon
    // does. Sorted rather than emitted in scan order because the scan is a box
    // and the player sees a sphere.
    this.pending = kept.sort((left, right) => this.window.priority(left) - this.window.priority(right));
  }

  /** Adds a chunk to the remesh queue, at the front when a dig is waiting on it. */
  private enqueue(coord: ChunkCoord, urgent: boolean): void {
    const key = chunkKey(coord);

    if (this.pendingKeys.has(key)) {
      return;
    }

    this.pendingKeys.add(key);

    if (urgent) {
      this.pending.unshift(coord);
    } else {
      this.pending.push(coord);
    }
  }

  /** Meshes up to the per-drain budget from the queue. */
  private meshPending(): void {
    let budget = MESH_BUDGET_PER_DRAIN;

    while (budget > 0 && this.pending.length > 0) {
      const coord = this.pending.shift();

      if (coord === undefined) {
        break;
      }

      const key = chunkKey(coord);
      this.pendingKeys.delete(key);

      if (!this.window.contains(coord)) {
        // It left the window between being queued and being meshed. The
        // eviction pass has already removed whatever it had.
        continue;
      }

      budget -= 1;
      this.meshChunk(coord, key);
    }
  }

  /** Meshes one chunk, copies its buffers out of wasm memory, and frees the handle. */
  private meshChunk(coord: ChunkCoord, key: string): void {
    const mesh = this.sim.mesh_chunk(coord.cx, coord.cy, coord.z);
    const hasGeometry = !mesh.is_empty;
    const previous = this.meshed.get(key);

    this.meshed.set(key, { coord, hasGeometry });

    if (hasGeometry) {
      // Four copies out of linear memory, then the handle goes. The registry
      // validates the contract these satisfy on upload, which is why it is not
      // checked twice here (`render/chunkMeshes.ts`).
      this.updates.push({
        kind: "set",
        coord,
        buffers: {
          positions: mesh.positions(),
          normals: mesh.normals(),
          colors: mesh.colors(),
          indices: mesh.indices(),
        },
      });
    } else if (previous?.hasGeometry === true) {
      // A re-mesh that came back empty — dug out entirely, or the last visible
      // face was covered. Without this the registry would keep drawing what was
      // there before.
      this.updates.push({ kind: "remove", coord });
    }

    mesh.free();
  }

  /**
   * Returns how far down and how far up each of a set of chunk columns reaches.
   *
   * **Two questions with two different answers, and the asymmetry is the whole
   * of wave 1.6's item 10.**
   *
   * The *floor* is sampled: four corners and the middle of each column rather
   * than all 256 tiles, because measured against the true per-chunk range on
   * seed 0x5eed five samples miss it by at most one tile, the band that uses it
   * has six tiles of margin under it, and 256 lookups per column would be 20,000
   * per window rebuild for that one tile.
   *
   * The *ceiling* is asked. It used to be sampled the same way — the highest of
   * the same five ground levels, plus a margin — and a sampled ceiling is a
   * different kind of wrong from a sampled floor: a tile of slack under the
   * ground is a tile of extra air, while a gable between two probes is a roof
   * that is never meshed at all. `column_reach` answers for the whole 16×16
   * footprint and counts what stands on the ground as well as the ground, which
   * is the number the mesh band actually wanted all along
   * (`sim_core::Snapshot::column_reach`).
   *
   * **And it is asked for the whole square at once**, which is the simulation's
   * own condition rather than a micro-optimisation: the answer is read out of a
   * town's plan, laying a plan out is a third of a second, and a window's
   * eighty-one columns asked one at a time before any ground exists cost 2,413
   * ms against 45 ms asked together (9 ms once the first interest has folded the
   * plans). Flat `cx, cy` pairs out and one level per column back, by index
   * (`crates/sim-wasm/src/sim.rs`).
   *
   * Every column is asked about once ever: `world/streaming.ts` memoises the
   * pair, which it is allowed to do because neither half can rise (see
   * {@link ColumnSpan.reach}).
   */
  private columnSpans(
    columns: readonly (readonly [number, number])[],
  ): readonly ColumnSpan[] {
    const asked = new Int32Array(columns.length * 2);

    for (const [slot, [cx, cy]] of columns.entries()) {
      asked[slot * 2] = cx;
      asked[slot * 2 + 1] = cy;
    }

    const reaches = this.sim.column_reach(asked);
    const last = CHUNK_SIZE - 1;
    const middle = CHUNK_SIZE >> 1;

    return columns.map(([cx, cy], slot) => {
      const x = cx * CHUNK_SIZE;
      const y = cy * CHUNK_SIZE;
      let lowest = Number.POSITIVE_INFINITY;

      for (const [sx, sy] of [
        [x, y],
        [x + last, y],
        [x, y + last],
        [x + last, y + last],
        [x + middle, y + middle],
      ] as const) {
        lowest = Math.min(lowest, this.sim.ground_level(sx, sy));
      }

      // `?? lowest` rather than a throw, on this file's standing rule for
      // reading a flat array back by index: a short answer is a bug on the
      // other side of the wall, and a reach at the ground meshes a thin band
      // rather than an infinite one.
      return { lowest, reach: reaches[slot] ?? lowest };
    });
  }
}

/*
 * ===================== The witnessing surface ======================
 *
 * {@link WitnessSource} over `sim-wasm`, replacing the hand-authored fixture
 * waves 1B and 2B built the client against (`world/fixture.ts`, still reachable
 * behind `?fixture=1` for a visual nobody wants to hunt a seed for). Nothing
 * above `world/` learns which one it is talking to, which was the point of
 * writing the interface down first.
 *
 * Three rules shape the translation, and all three are the plan's:
 *
 * - **Bulk numbers come across as flat typed arrays and are decoded by index**
 *   (decision 11). Every stride and every `-1` below is transcribed from
 *   `crates/sim-wasm/src/flatten.rs`, which states each layout once. Nothing
 *   here guesses at one — and the one layout with **no** stride to transcribe
 *   says so in as many words ({@link SAGA_CITE_HEADER}): it is walked by the
 *   counts it carries, which is what lets it be right about a width neither side
 *   of this boundary owns.
 * - **Strings are pulled per call, on an interaction or a day boundary.** Names,
 *   lines and inscriptions are method calls into the boundary; none of them is
 *   on a per-frame path, and the caches below exist so the bulk lists are not
 *   either.
 * - **The client never composes a sentence about a record.** A spoken line and
 *   an inscription arrive whole from `sim-core`'s projections (decision 9); what
 *   this file assembles is *labels* — "refugees from Dagtorvik" is composed
 *   sim-side, and the only text built here is nothing at all.
 *
 * What it does decide is presentation, and each piece says so where it is: how
 * far around town to look for props, how long a column of refugees is still
 * walking, how long a caravan is, where a market board stands, and which way a
 * prop faces.
 *
 * Phase 4 wave 2A finishes the surface — the caravans on the roads, the
 * spotlight feed, a line about a named record, an introduction — and re-keys the
 * caches on the visited settlement as well as on the calendar (decision 10a).
 * The four stubs wave 1B left here are gone; what replaced each of them answers
 * out of the simulation, and the rule they were written under still stands for
 * anything that comes next: **answer nothing, never something plausible.**
 */

/** The live {@link WitnessSource}. Reads the same handle {@link WasmWorldSource} ticks. */
export class WasmWitnessSource implements WitnessSource {
  private readonly sim: WasmSim;

  /** The world source, for the date and the day-boundary stamp it already holds. */
  private readonly world: WasmWorldSource;

  /*
   * ===================== The two bulk caches, and their keys =================
   *
   * Every list below is slow bulk and is pulled on a day boundary, never per
   * tick (plan decision 11). What differs between the two caches is *what could
   * have changed the answer*, which is exactly what a cache key has to name:
   *
   * - the map is the same map wherever the player is standing, so it is keyed on
   *   {@link WasmWorldSource.stamp} alone — the day count and the record count;
   * - everything else is a read *about the visited settlement*, so it is keyed
   *   on the stamp **and** on {@link WasmWitnessSource.here}.
   *
   * That second key is Phase 4 decision 10a, and it is a carried bug rather than
   * a refinement: with one stamp-only cache, walking from one town into the next
   * kept serving the town behind you — its roster standing in this street, its
   * dead on this ground — until the calendar happened to turn, which is up to a
   * whole in-game day away. Nothing about that was visible as a bug; it looked
   * like the next town being oddly familiar. The two lists this wave adds would
   * have joined it, and a caravan is worse: it is drawn walking a road between
   * two settlements that are no longer either end of anything nearby.
   *
   * Phase 5's retune made that window thirty times longer — a day is now
   * something a player can live inside (plan decision 7) — which is worth
   * knowing about every cache in this file: anything keyed on the calendar alone
   * is keyed on a very slow clock.
   *
   * The market is deliberately in neither: a price is derived from the
   * settlement's ledger on every call and moves without a record being written,
   * so a cache keyed on records would show a stale board.
   */

  /** The map, which is the same map from everywhere. Keyed on the stamp. */
  private worldCache: {
    stamp: number;
    towns: readonly SettlementView[] | null;
    routes: readonly RouteLeg[] | null;

    /**
     * The towns in ruin, which is the same list from everywhere.
     *
     * In this cache rather than the visited settlement's for the reason the map
     * is: a ruin is a fact about a town, not about where the player is standing,
     * and a client that marks them on paper wants all of them.
     *
     * The stamp is the right key for a *window* as well as for a list: ruin
     * expires five years after the sack with no record written to say so, and
     * the stamp counts days as well as records — so it moves at least once a
     * day, which is the finest grain the answer changes at.
     */
    ruins: readonly RuinView[] | null;

    /**
     * The Book of Eras, which is the world's own history and therefore the same
     * book from everywhere.
     *
     * In this cache rather than the visited settlement's for the map's reason,
     * and it is the entry that earns a cache most: reading the whole book is one
     * boundary call per age plus four more per age for its two names, its lines
     * and its citations, and each of them re-derives the projection over the
     * whole log (`crates/sim-wasm`'s `saga_eras`). That is affordable once at a
     * shelf and not affordable on a loop, so it is paid once per stamp.
     */
    saga: readonly SagaEra[] | null;
  };

  /** The visited settlement's reads. Keyed on the stamp *and* on where. */
  private hereCache: {
    stamp: number;

    /**
     * Which settlement these lists are about, or `null` for nowhere.
     *
     * Nowhere is a key like any other and every list under it is empty: the
     * reads below are all **claims about a place** — whose roster this is, whose
     * shelf, what is worth meeting here — and there is no place to claim
     * (`world/types.ts`, {@link WitnessSource.here}).
     */
    at: SettlementId | null;
    roster: readonly RosterEntry[] | null;

    /**
     * The books on this town's shelves.
     *
     * Here rather than in the world cache, and that is the `here()` key doing
     * its job (decision 10a): a shelf is a read *about a settlement*, so a
     * stamp-only cache would show the last town's books in this town's street
     * until the calendar happened to turn. It is also why a book the player
     * places appears: leaving one writes a record, the record moves the stamp,
     * and the next read is a fresh one.
     */
    chronicles: readonly ChronicleView[] | null;

    /**
     * The last spotlight question and its answer.
     *
     * Keyed by the question as well, because {@link WitnessSource.beats} takes a
     * settlement and a window rather than reading `here` — the director asks
     * about where the player is, but the contract does not make it, so a cache
     * that assumed so would answer a question nobody asked.
     */
    beats: { key: string; offered: readonly SpotlightBeat[] } | null;
  };

  /**
   * The road's own reads. Keyed on the stamp *and* on the nearest town.
   *
   * **A second key, and the only one this file has that is not a place claim**
   * ({@link WasmWitnessSource.nearestTown}). Everything in {@link
   * WasmWitnessSource.hereCache} answers *"whose town is this"* and is empty out
   * in the country, because the honest answer to that question between two
   * settlements is nobody's. These three answer a different question — *"what is
   * standing on the ground around me"* — and the honest answer to that is never
   * nothing: a grave beside a road is beside the road whether or not the town it
   * belongs to is being held up, and a column of refugees walking the road is
   * walking it.
   *
   * Splitting them is item 6's one amendment to rider 4 (owner-ruled
   * 2026-08-28, after the first cut of this chunk emptied the countryside): the
   * *authority* over which town the player is in is one answer and stays one,
   * and this is a **scope** rather than an authority — the nearest town is only
   * ever used to ask the simulation which corner of the world to look in.
   */
  private nearCache: {
    stamp: number;

    /**
     * Which town's corner of the world these lists are about.
     *
     * `-1` in a world with no towns, which is the only case that empties them —
     * and then there is nothing on any road anyway.
     */
    near: SettlementId;
    features: readonly AftermathFeature[] | null;
    groups: readonly RefugeeGroup[] | null;
    pulses: readonly TradePulse[] | null;
  };

  /**
   * The record and kind behind each feature id this session has handed out.
   *
   * A feature is derived rather than stored, so it has no id of its own: its
   * identity is the record it cites and its kind, which is the pair
   * `aftermath::near` de-duplicates on. The client's contract wants a number
   * ({@link AftermathFeature.id}) that a prop can be keyed by, so this hands out
   * a counter and remembers what it stands for — which is also what
   * {@link inscription} needs to ask the simulation again.
   */
  private readonly featureRefs = new Map<number, { event: EventId; kind: number }>();
  private readonly featureIds = new Map<string, number>();

  constructor(sim: WasmSim, world: WasmWorldSource) {
    this.sim = sim;
    this.world = world;
    this.worldCache = { stamp: world.stamp(), towns: null, routes: null, ruins: null, saga: null };
    this.hereCache = {
      stamp: world.stamp(),
      // Nowhere, until the world has promoted somewhere: promotion happens
      // inside the first tick and this object is built before it, so a session
      // genuinely is nowhere for the length of its boot sequence.
      at: null,
      roster: null,
      chronicles: null,
      beats: null,
    };
    // The road's own cache, empty and keyed on nowhere-in-particular: the first
    // read fills the key in, and `-1` matches no town, which is the honest
    // reading of a session that has not been told where the player is standing.
    this.nearCache = { stamp: world.stamp(), near: -1, features: null, groups: null, pulses: null };
  }

  date(): WorldDateView {
    // Off the tick summary the client already holds, not out of the boundary:
    // the date changes once a day and a call per frame for it would be the
    // exact traffic decision 11 exists to prevent. The finer clock — how far
    // through that day it is — is a different question with a different cadence,
    // and it is asked rather than kept ({@link WasmWitnessSource.timeOfDay}).
    return this.world.today();
  }

  timeOfDay(): number | null {
    // Straight through, per ask, and deliberately uncached: this is the one read
    // on the surface that changes every tick, and the client asks it a couple of
    // times per rendered frame to draw the sky. It costs a remainder and a
    // division inside the boundary (`WasmSim::time_of_day`), which is cheaper
    // than the bookkeeping a cache would need to know it was stale.
    return this.sim.time_of_day();
  }

  playerFigure(): FigureId | null {
    // `-1` is the boundary's spelling of nobody, the same one a caravan with no
    // talker uses. Uncached because it is a scalar and because the moment it
    // changes — the claim landing — is a moment the client is watching for.
    const figure = this.sim.player_figure();

    return figure === NONE ? null : figure;
  }

  here(): SettlementId | null {
    // **The promotion slot, and no arithmetic at all** (item 6's rider 4, ruled
    // 2026-08-28). This used to be the nearest settlement to the interest
    // centre with no distance bound, which is a second answer to a question the
    // simulation already answers — and the window in which the two disagreed is
    // where every symptom of the item-6 investigation fired. There is one
    // answer now and this is a forward to it (`world/types.ts`).
    //
    // What the old arithmetic bought was a town for a player standing between
    // two of them; what it cost was a market panel, a roster and a share-word
    // offer for a town the simulation was not holding up. The halo is what
    // makes the loss small: a town is promoted while the drawn window touches
    // its ground, which reaches further out than a player would call "in town".
    //
    // The one thing it bought that was worth keeping is a *scope* rather than an
    // answer — which corner of the world the road's own reads look in — and that
    // is kept, separately and privately, as
    // {@link WasmWitnessSource.nearestTown}. It is not this and must never
    // become it.
    return this.promoted();
  }

  settlements(): readonly SettlementView[] {
    return this.towns();
  }

  roadMouth(from: SettlementId, to: SettlementId): { readonly x: number; readonly y: number } | null {
    const at = this.sim.road_mouth(from, to);
    const x = at[0];
    const y = at[1];

    return x === undefined || y === undefined ? null : { x, y };
  }

  routes(): readonly RouteLeg[] {
    const cache = this.freshWorld();

    if (cache.routes === null) {
      const legs: RouteLeg[] = [];
      const seen = new Set<string>();

      for (const town of this.towns()) {
        const flat = this.sim.routes(town.settlement);

        for (let i = 0; i + 1 < flat.length; i += ROUTE_STRIDE) {
          const to = flat[i] ?? 0;
          // The road graph is symmetric — a road out of A to B is a road out of
          // B to A — and the client draws a line, which has no direction. One
          // leg per pair, keyed on the ordered pair so the key does not depend
          // on which end was walked first.
          const key = `${Math.min(town.settlement, to)}:${Math.max(town.settlement, to)}`;

          if (seen.has(key)) {
            continue;
          }

          seen.add(key);
          legs.push({ from: town.settlement, to });
        }
      }

      cache.routes = legs;
    }

    return cache.routes;
  }

  roster(): readonly RosterEntry[] {
    const cache = this.freshHere();
    const at = cache.at;

    if (at === null) {
      // Nowhere has no living named figures — see {@link WasmWitnessSource.here}.
      // Every read below this one takes the same branch and it is written out
      // each time rather than hoisted, because each of them is a different
      // question with the same answer.
      return [];
    }

    if (cache.roster === null) {
      const flat = this.sim.roster(at);
      const people: RosterEntry[] = [];

      for (let i = 0; i + ROSTER_STRIDE - 1 < flat.length; i += ROSTER_STRIDE) {
        const figure = flat[i] ?? 0;

        people.push({
          figure,
          name: this.named(figure),
          settlement: at,
          facets: {
            anxiety: flat[i + 1] ?? 0,
            bravery: flat[i + 2] ?? 0,
            trust: flat[i + 3] ?? 0,
            gregariousness: flat[i + 4] ?? 0,
          },
        });
      }

      cache.roster = people;
    }

    return cache.roster;
  }

  market(settlement: SettlementId): readonly MarketRow[] {
    const flat = this.sim.market(settlement);
    const rows: MarketRow[] = [];

    for (let i = 0; i + MARKET_STRIDE - 1 < flat.length; i += MARKET_STRIDE) {
      const commodity = flat[i] ?? 0;
      const band = flat[i + 2] ?? 0;
      const shock = flat[i + 3] ?? NONE;
      const cause = flat[i + 4] ?? NONE;
      const move = flat[i + 5] ?? 0;

      rows.push({
        commodity,
        commodityName: commodity_name(commodity),
        priceIndex: flat[i + 1] ?? 0,
        band,
        bandName: stock_band_name(band),
        shock:
          shock === NONE
            ? null
            : {
                event: shock,
                // `PriceShock::cause` verbatim, and the whole reason a board is
                // a channel rather than a gauge: a shock with a cause is the war
                // arriving as a number (plan decision 7). A shock without one is
                // weather, and the panel says so.
                cause: cause === NONE ? null : cause,
                move,
                moveName: price_move_name(move),
                when: { year: flat[i + 6] ?? 0, dayOfYear: flat[i + 7] ?? 0 },
              },
      });
    }

    return rows;
  }

  rumors(settlement: SettlementId): readonly RumorEntry[] {
    const flat = this.sim.rumors(settlement);
    const heard: RumorEntry[] = [];

    // Backwards: the store is held oldest first, which is the order the world
    // hash folds it in, and this surface promises freshest first. Reversing is
    // the client's business precisely because the simulation must not hold an
    // opinion about a display order.
    for (let i = flat.length - RUMOR_STRIDE; i >= 0; i -= RUMOR_STRIDE) {
      const via = flat[i + 4] ?? NONE;

      heard.push({
        event: flat[i] ?? 0,
        learned: { year: flat[i + 1] ?? 0, dayOfYear: flat[i + 2] ?? 0 },
        hops: flat[i + 3] ?? 0,
        via: via === NONE ? null : via,
      });
    }

    return heard;
  }

  /**
   * Everything the log has left standing around the player.
   *
   * **Keyed on the nearest town and not on `here()`**, which is the one place in
   * this file where those are different questions
   * ({@link WasmWitnessSource.nearCache}). A grave, a cairn and a burned
   * farmstead are read in a two-hundred-tile radius around a town's centre —
   * deliberately wide, so that "props are there when the player walks out along
   * the road" ({@link FEATURE_RADIUS_TILES}) — and keying that on the promotion
   * slot emptied the whole country between two settlements, which is a
   * regression against a recordless layer the owner has played and kept.
   *
   * **The honest long-term shape is a window around the *player*.** What this
   * read wants to be asked is *"what is standing within so many tiles of this
   * column"*, which needs no settlement at all and would make the nearest-town
   * key disappear rather than be justified. That is a change to the read on both
   * sides of the wall and a chunk of its own; it is filed in
   * `docs/decisions/witness.md` and this is the note it is filed against.
   */
  features(): readonly AftermathFeature[] {
    const cache = this.freshNear();

    if (cache.features === null) {
      const here = this.towns().find((town) => town.settlement === cache.near);

      if (here === undefined) {
        cache.features = [];

        return cache.features;
      }

      const flat = this.sim.features(here.x, here.y, FEATURE_RADIUS_TILES);
      const found: AftermathFeature[] = [];

      for (let i = 0; i + FEATURE_STRIDE - 1 < flat.length; i += FEATURE_STRIDE) {
        const kind = flat[i] ?? 0;
        const event = flat[i + 3] ?? 0;
        const x = flat[i + 1] ?? 0;
        const y = flat[i + 2] ?? 0;

        found.push({
          id: this.featureId(event, kind),
          kind,
          // The z is the client's: a feature stands on a world-field column and
          // the ground under it is `ground_level`'s answer, which is the same
          // query the player was placed with.
          position: { x, y, z: this.sim.ground_level(x, y) },
          facing: facingOf(event, kind),
          refs: [event],
        });
      }

      // The market board, which is not aftermath: nothing in the log put it
      // there, so the client places it (see `MARKET_BOARD_OFFSET`). It carries
      // the records its own panel is about, so that reading the board and
      // reading the journal afterwards agree about what was learned.
      const board = this.market(here.settlement)
        .map((row) => row.shock?.cause ?? row.shock?.event ?? null)
        .filter((event): event is EventId => event !== null);

      if (board.length > 0) {
        // **The market's own doorstep** (wave 1.5's graybox ruling): a stall is
        // a lot with a street column outside its door, which is exactly where
        // somebody stands to read what is nailed up — and it is a column the
        // town plan chose, so it is never in a wall and never in the middle of
        // the road. The first standing market, which is the plan's own
        // materialisation order and therefore the one nearest the crossing
        // (`crates/sim-core/src/townplan.rs`).
        const stall = this.lots(here.settlement).find(
          (lot) => this.lotName(lot.kind) === MARKET_BOARD_LOT,
        );
        const x = stall?.doorstep.x ?? here.x + MARKET_BOARD_OFFSET.x;
        const y = stall?.doorstep.y ?? here.y + MARKET_BOARD_OFFSET.y;

        found.push({
          id: this.featureId(board[0] ?? 0, FEATURE_MARKET_BOARD),
          kind: FEATURE_MARKET_BOARD,
          position: { x, y, z: this.sim.ground_level(x, y) },
          // **Facing the street, not the wall it stands against.** A board is a
          // monument: it is read from in front, and where its front points is
          // where a player has to stand to read it (`render/props.ts`'s facing
          // convention, and `witness/witness.ts`'s `vantageFor`). At a guessed
          // offset in the middle of town any facing would do; at a doorstep
          // there is a building one tile behind it, so the face has to turn the
          // way the door does — which is the doorstep seen from the work tile
          // inside ({@link TownLot}).
          facing: stall === undefined ? 0 : facingOutward(stall),
          refs: board,
        });
      }

      cache.features = found;
    }

    return cache.features;
  }

  refugeeGroups(): readonly RefugeeGroup[] {
    const cache = this.freshNear();

    if (cache.groups === null) {
      const flat = this.sim.flights(cache.near, REFUGEE_WINDOW_DAYS);
      const walking: RefugeeGroup[] = [];

      for (let i = 0; i + FLIGHT_STRIDE - 1 < flat.length; i += FLIGHT_STRIDE) {
        walking.push({
          // The battle they fled is the group's identity as well as its
          // citation: two columns on the same road out of the same sack are the
          // same record, and the client keys a body by this.
          id: flat[i] ?? 0,
          from: flat[i + 1] ?? 0,
          to: flat[i + 2] ?? 0,
          size: flat[i + 3] ?? 0,
          departed: { year: flat[i + 4] ?? 0, dayOfYear: flat[i + 5] ?? 0 },
          refs: [flat[i] ?? 0],
        });
      }

      cache.groups = walking;
    }

    return cache.groups;
  }

  /*
   * ========================= Phase 4: the live reads =========================
   *
   * The four reads wave 1B added to {@link WitnessSource} as stubs, over the
   * boundary wave 2A grew for them. Everything above them is unchanged, which is
   * the claim decision 2 and decision 3 both rest on: a caravan is a new *thing
   * to meet* rather than a new mechanism, and an imported price shock is a
   * `PriceShock` like any other — it arrives through the market layout Phase 3
   * already shipped, citing the battle a refugee two towns away is fleeing.
   */

  pulses(): readonly TradePulse[] {
    const cache = this.freshNear();

    if (cache.pulses === null) {
      // The roads out of the town the player is *nearest*, both ways along each:
      // a pulse is a round trip, so the column that left this morning is as much
      // on this town's road as the one walking in. Asking about one town rather
      // than about the world is the same scoping the refugee read has — a
      // caravan two hundred tiles beyond the fog is a body nobody can walk up
      // to, and drawing it would be paying for a crowd nobody meets. The
      // *nearest* and not the promoted one, because a road is walked between
      // two towns and neither of them may be up
      // ({@link WasmWitnessSource.nearestTown}).
      const flat = this.sim.pulses(cache.near, CARAVAN_WINDOW_DAYS);
      const walking: TradePulse[] = [];

      for (let i = 0; i + PULSE_STRIDE - 1 < flat.length; i += PULSE_STRIDE) {
        const talker = flat[i + 5] ?? NONE;

        walking.push({
          // The sim's own handle for the caravan — the road and the turn folded
          // together — and not an index into this list, because the list is
          // re-read every day and a body keyed by a position in it would swap
          // places with its neighbour.
          id: flat[i] ?? 0,
          from: flat[i + 1] ?? 0,
          to: flat[i + 2] ?? 0,
          day: { year: flat[i + 3] ?? 0, dayOfYear: flat[i + 4] ?? 0 },
          talker: talker === NONE ? null : talker,
          // One name per caravan, pulled with the list: the talker is a figure
          // of the *origin* settlement, so `roster()` — which is the visited
          // town's — cannot answer for them.
          talkerName: talker === NONE ? "" : this.named(talker),
        });
      }

      cache.pulses = walking;
    }

    return cache.pulses;
  }

  beats(settlement: SettlementId, windowDays: number): readonly SpotlightBeat[] {
    const cache = this.freshHere();
    const key = `${settlement}:${windowDays}`;

    if (cache.beats === null || cache.beats.key !== key) {
      const flat = this.sim.beats(settlement, windowDays);
      const offered: SpotlightBeat[] = [];

      for (let i = 0; i + BEAT_STRIDE - 1 < flat.length; i += BEAT_STRIDE) {
        const subject = flat[i + 2] ?? NONE;
        const refs: EventId[] = [];

        // The citations ride at a fixed stride with the unused slots spelled
        // `NONE`, so this walk is the whole of the decoding: a beat cites one
        // record or two, and which it is does not change the row's width
        // (`crates/sim-wasm/src/flatten.rs`).
        for (let slot = 0; slot < BEAT_CITED; slot += 1) {
          const event = flat[i + 5 + slot] ?? NONE;

          if (event !== NONE) {
            refs.push(event);
          }
        }

        offered.push({
          id: flat[i] ?? 0,
          channel: flat[i + 1] ?? 0,
          subject: subject === NONE ? null : subject,
          earliest: { year: flat[i + 3] ?? 0, dayOfYear: flat[i + 4] ?? 0 },
          refs,
        });
      }

      // In the order the projection returned them, which is a judgement about
      // what is worth showing and not this client's to re-sort (decision 1).
      cache.beats = { key, offered };
    }

    return cache.beats.offered;
  }

  chronicles(settlement: SettlementId): readonly ChronicleView[] {
    const cache = this.freshHere();

    // A shelf somewhere else is answered and not kept. The cache is the visited
    // settlement's — like the roster and the props — and a caller may ask about
    // any town, so caching another one's answer under this key would hand the
    // wrong town's books back on the next call. That is `notice`'s hazard in a
    // different shape, and the same answer: the pair is the identity.
    if (settlement !== cache.at) {
      return this.shelf(settlement);
    }

    cache.chronicles ??= this.shelf(settlement);

    return cache.chronicles;
  }

  chronicleLines(chronicle: number): readonly string[] {
    // Uncached, like every other string on this surface: a book is opened on an
    // interaction, and it comes back whole — one string per record it cites,
    // composed by `sim-core` in the written register. Nothing here assembles a
    // page out of parts (devlog #2).
    //
    // Keyed by the book alone, unlike a market notice: a chronicle carries the
    // town it stands in, so the projection reads the ground off the book rather
    // than off the caller.
    return this.sim.chronicle_lines(chronicle);
  }

  chronicleEntries(chronicle: number): readonly EventId[] {
    // Uncached beside `chronicleLines`, and for the same reason: a book is
    // opened on an interaction, and its citations cannot change — a chronicle is
    // appended to the shelf once and never edited (`sim_core::history`'s
    // `Chronicles`). The array is a copy out of wasm memory like every other
    // flat list, so it is the caller's to keep.
    return Array.from(this.sim.chronicle_entries(chronicle));
  }

  ruins(): readonly RuinView[] {
    const cache = this.freshWorld();

    if (cache.ruins === null) {
      const flat = this.sim.ruins();
      const ruined: RuinView[] = [];

      for (let i = 0; i + RUIN_STRIDE - 1 < flat.length; i += RUIN_STRIDE) {
        ruined.push({
          settlement: flat[i] ?? 0,
          // The battle the ruin is drawing. A client that shows it can put the
          // same `EventId` in the journal a rumour and a grave put there, which
          // is the traceability the whole surface is held to.
          sack: flat[i + 1] ?? 0,
          since: { year: flat[i + 2] ?? 0, dayOfYear: flat[i + 3] ?? 0 },
        });
      }

      cache.ruins = ruined;
    }

    return cache.ruins;
  }

  /*
   * ===================== Phase 6: the Book of Eras ==========================
   *
   * One projection read through five exports, and one knob. The decoding rule is
   * the file's own — numbers flat and by index, strings per call — with a single
   * departure worth naming: the citation array has a **variable** stride, so it
   * is walked by its own counts rather than by a number written down here
   * ({@link SAGA_CITE_HEADER}).
   *
   * What this file assembles is the *pairing*, and nothing else. The sentences,
   * the two names an age has and the records behind each line are all the
   * simulation's; the client zips two parallel lists into the shape the panels
   * read, and composes no word of it.
   */

  saga(): readonly SagaEra[] {
    const cache = this.freshWorld();

    cache.saga ??= this.book();

    return cache.saga;
  }

  /*
   * ================ Phase 6: the lives the world has to offer ================
   *
   * The read half of the birth door (decision 3). Same decoding rule as
   * everything above — numbers flat and by index, strings per call — with the
   * per-card claims zipped in, exactly as a chapter's citations are zipped into
   * its lines: the boundary hands over the two halves separately and this file
   * assembles the pairing and nothing else.
   *
   * **Uncached, and that is deliberate.** The saga is cached because a book is
   * opened again and again in one session; the birth screen is read once, before
   * play, and the one moment a cache would be wrong is the moment it matters —
   * a *bound* birth appends no record, so the stamp does not move, and a stale
   * list would go on offering the life the player has just taken.
   */

  origins(): readonly OriginCard[] {
    const flat = this.sim.origins();
    const offered: OriginCard[] = [];

    for (let i = 0; i + ORIGIN_STRIDE - 1 < flat.length; i += ORIGIN_STRIDE) {
      const card = i / ORIGIN_STRIDE;
      const settlement = flat[i + 1] ?? 0;
      const mother = handleOr(flat[i + 3]);
      const father = handleOr(flat[i + 4]);
      const figure = handleOr(flat[i + 5]);
      const house = handleOr(flat[i + 6]);

      offered.push({
        kind: flat[i] ?? NONE,
        // The word the simulation labels the door with, pulled with the row:
        // three cards' worth of strings on a screen read once is not a
        // per-frame path, and it is what a trace panel prints.
        kindName: origin_kind_name(flat[i] ?? 0),
        settlement,
        settlementName: this.sim.settlement_name(settlement),
        // The sack, or the birth. What the card *says* is this record's own
        // sentence — nothing here composes one.
        cites: flat[i + 2] ?? 0,
        mother,
        // One name per person named, on the same terms as a book's author: a
        // parent is often not on the visited town's roster, so `roster()`
        // cannot answer for them.
        motherName: mother === null ? "" : this.named(mother),
        father,
        fatherName: father === null ? "" : this.named(father),
        figure,
        figureName: figure === null ? "" : this.named(figure),
        house,
        houseName: house === null ? "" : this.sim.house_name(house),
        // Asked for by the card's row index, which is the one place an index is
        // a legitimate argument on this surface: it names a row of *this* call's
        // list. What goes back into the simulation is never it.
        claims: this.seats(this.sim.origin_claims(card)),
      });
    }

    return offered;
  }

  claims(figure: FigureId): readonly OriginClaim[] {
    // Uncached beside `origins`, and for `chronicleEntries`' reason as well as
    // that one: it is asked about a figure on an interaction — the player, after
    // the birth — and it is four rows at most.
    return this.seats(this.sim.claims(figure));
  }

  wakingLine(origin: number, name: string): string {
    // The row index and the typed name go over and a whole sentence comes back;
    // nothing is decided on this side. The index names a row of `origins()`'
    // own list, which is the one place an index is a legitimate argument on this
    // surface (`origins` above says so of `origin_claims`), and the name is
    // normalised sim-side so the sentence carries the name the record will carry
    // (`WasmSim::waking_line`).
    //
    // Uncached, on `claims`' terms: it is asked once, at the moment a life is
    // chosen, and a session asks it a handful of times at most.
    return this.sim.waking_line(origin, name);
  }

  setSalienceDecay(days: number | null): void {
    // `null` crosses as a negative, which is the boundary's own spelling of
    // "the lens is off" — and it has to be a value rather than an absence,
    // because zero is a real setting the simulation has ruled on (everything is
    // forgotten) and `?decay=0` must not become `?decay=off` on the way through
    // (`crates/sim-wasm/src/convert.rs`'s `half_life_from_f64`).
    this.sim.set_salience_decay(days ?? NONE);

    // Every cache, because the knob changes what the mouths say and what the
    // square offers — a beat list read before it was turned is a beat list about
    // things the town has since forgotten. Applied at boot in practice
    // (`witness/saga.ts`), so this costs one re-read of whatever had been asked
    // for before that; correctness is worth more than the read.
    this.worldCache = {
      stamp: this.world.stamp(),
      towns: null,
      routes: null,
      ruins: null,
      saga: null,
    };
    this.hereCache = {
      stamp: this.world.stamp(),
      at: this.hereCache.at,
      roster: null,
      chronicles: null,
      beats: null,
    };
    // The road's too, and it is the one that needs saying: nothing on it is a
    // *projection over records* — a grave is where a death was, a column is a
    // flight — so the lens cannot change any of them. It is dropped anyway,
    // because a cache that is emptied on a rule ("every cache, whenever the
    // reading changes") is a cache nobody has to reason about, and this costs
    // three lists once a session.
    this.nearCache = {
      stamp: this.world.stamp(),
      near: this.nearCache.near,
      features: null,
      groups: null,
      pulses: null,
    };
  }

  speak(figure: FigureId): SpokenLine | null {
    return this.said(this.sim.speak(figure));
  }

  speakOf(figure: FigureId, event: EventId): SpokenLine | null {
    // The record named rather than chosen, which is what a crier is — and
    // `undefined` when this figure's settlement has never heard of it, because
    // a speaker who said something else would put the wrong id in the journal.
    return this.said(this.sim.speak_of(figure, event));
  }

  speakPulse(pulse: number): SpokenLine | null {
    // The pulse's own id, which is what the simulation looks the caravan up by:
    // the traveller, their settlement's freshest news and their hops are all
    // read off the sender, sim-side (`crates/sim-wasm`'s `speak_pulse`).
    return this.said(this.sim.speak_pulse(pulse));
  }

  introduce(figure: FigureId): string {
    // Composed by `sim-core` out of the figure's own record, exactly as a
    // spoken line and an inscription are: the client has never assembled a
    // sentence about anybody and does not start here (plan decision 9).
    return this.sim.introduction(figure);
  }

  byname(figure: FigureId): string {
    // Straight through and uncached, like every other scalar read on this
    // surface: the answer is a projection over that one figure's own records
    // (`sim_core::history::byname`), and the empty string for the many people
    // whose records have earned them nothing to be called. The same export the
    // name plate's {@link WasmWitnessSource.named} pairs with a name, asked on
    // its own here — a page that shows an epithet shows `sim-core`'s string and
    // never one this client assembled.
    return this.sim.figure_byname(figure);
  }

  speakGroup(group: number): SpokenLine | null {
    // A group is named by the battle it fled, so the id is the record — which
    // is what the simulation's projection takes.
    return this.said(this.sim.speak_flight(group));
  }

  inscription(feature: number): string {
    const refs = this.featureRefs.get(feature);

    return refs === undefined ? "" : this.sim.inscription(refs.event, refs.kind);
  }

  describe(event: EventId): string {
    return this.sim.describe(event);
  }

  notice(settlement: SettlementId, event: EventId): string {
    // The board's own projection, not the chronicle's: `describe` above is the
    // debug half and this is what a player reads (`crates/sim-wasm`'s `notice`).
    //
    // The settlement goes through because a board belongs to a town and speaks
    // with that ground: the same record reads "this town was stormed" here and
    // names the place one town over. Nothing is decided on this side of the
    // boundary — the pair goes over and a whole sentence comes back.
    return this.sim.notice(settlement, event);
  }

  /*
   * ================ Phase 7: the place the player is standing ================
   *
   * Seven reads at the local tier's own scale, decoded by the file's own rule —
   * numbers flat and by index, strings per call.
   *
   * **None of them is cached, and that is the difference from everything above.**
   * The witnessing caches hang on a *record count* ({@link WasmWorldSource.stamp})
   * because the abstract tier only appends on a day boundary; the marketplace
   * moves on a job pulse and a body moves every tick, so a cache keyed on the
   * same stamp would hand a scene yesterday's square. What keeps the cost honest
   * instead is the caller's cadence: the client re-reads the board and the bodies
   * on its own scene tick and the rest on an interaction
   * (`witness/witness.ts`), which is the same discipline decision 11 asks for
   * with a faster clock behind it.
   */

  promoted(): SettlementId | null {
    return handleOr(this.sim.promoted_settlement());
  }

  jobs(): readonly JobPost[] {
    const flat = this.sim.jobs_at();
    const board: JobPost[] = [];

    for (let i = 0; i + JOB_STRIDE - 1 < flat.length; i += JOB_STRIDE) {
      board.push({
        kind: flat[i] ?? 0,
        site: { x: flat[i + 1] ?? 0, y: flat[i + 2] ?? 0, z: flat[i + 3] ?? 0 },
        worker: handleOr(flat[i + 4] ?? NONE),
      });
    }

    return board;
  }

  presentFigures(): readonly PresentFigure[] {
    const flat = this.sim.present_figures();
    const bodies: PresentFigure[] = [];

    for (let i = 0; i + PRESENCE_STRIDE - 1 < flat.length; i += PRESENCE_STRIDE) {
      bodies.push({
        figure: flat[i] ?? 0,
        at: { x: flat[i + 1] ?? 0, y: flat[i + 2] ?? 0, z: flat[i + 3] ?? 0 },
        // The two columns wave 2A appended: which life stage the simulation says
        // this body is in, and who is carrying it. Both ride the position
        // because a renderer needs all three to draw one body.
        stage: flat[i + 4] ?? NONE,
        heldBy: handleOr(flat[i + 5] ?? NONE),
      });
    }

    return bodies;
  }

  bodyScale(stage: number): number {
    // Uncached like {@link WasmWitnessSource.itemPortable} and for its reason: a
    // scalar off a four-row table, asked when a body's prop is assembled rather
    // than while a frame is being drawn.
    return body_scale_permille(stage);
  }

  walkTicksForStage(stage: number): number {
    return walk_ticks_per_step_for_stage(stage);
  }

  lifeStageWord(stage: number): string {
    return life_stage_word(stage);
  }

  lifeStagePortable(stage: number): boolean {
    return life_stage_portable(stage);
  }

  lifeStageFights(stage: number): boolean {
    return life_stage_fights(stage);
  }

  condition(figure: FigureId): number {
    return this.sim.condition(figure);
  }

  conditionWord(condition: number): string {
    return this.sim.condition_word(condition);
  }

  fleeingFrom(figure: FigureId): FigureId | null {
    return handleOr(this.sim.fleeing_from(figure));
  }

  engagedWith(figure: FigureId): FigureId | null {
    return handleOr(this.sim.engaged_with(figure));
  }

  answering(figure: FigureId): FigureId | null {
    return handleOr(this.sim.answering(figure));
  }

  leaderOf(figure: FigureId): FigureId | null {
    return handleOr(this.sim.leader_of(figure));
  }

  companions(figure: FigureId): readonly FigureId[] {
    return Array.from(this.sim.companions(figure));
  }

  followOffer(figure: FigureId): string {
    return this.sim.follow_offer(figure);
  }

  offersToFollow(figure: FigureId): boolean {
    return this.sim.offers_to_follow(figure);
  }

  partingLine(figure: FigureId): string {
    return this.sim.parting_line(figure);
  }

  stood(): TilePos | null {
    const held = this.sim.stood();

    return held.length < 3
      ? null
      : { x: held[0] ?? 0, y: held[1] ?? 0, z: held[2] ?? 0 };
  }

  afflictions(figure: FigureId): number {
    return this.sim.afflictions(figure);
  }

  deathOf(figure: FigureId): EventId | null {
    const record = this.sim.death_of(figure);

    return record < 0 ? null : record;
  }

  arrestOf(figure: FigureId): EventId | null {
    // The death read's own shape and its own sentinel (chunk Fx-5). Asked about
    // **one** figure — the player's — as part of the body's reading, so at worst
    // a few times a frame, and what it costs sim-side is a walk of that one
    // figure's own biography index rather than of the log.
    const record = this.sim.arrest_of(figure);

    return record < 0 ? null : record;
  }

  wantedFor(figure: FigureId): number | null {
    const crime = this.sim.wanted_for(figure);

    return crime < 0 ? null : crime;
  }

  crimeWord(crime: number): string {
    // Uncached like {@link WasmWitnessSource.lotName}, and for its reason: a
    // scalar off a five-row table, asked once when a guard is standing over
    // somebody rather than once a frame.
    return this.sim.crime_name(crime);
  }

  crimeOfTaking(pos: TilePos): number | null {
    // Straight through and uncached, like the two reads above it: a prompt asks
    // it about the one tile the crosshair is on, once a frame at worst, and the
    // answer is a walk of what is lying on that tile.
    const crime = this.sim.crime_of_taking(pos.x, pos.y, pos.z);

    return crime < 0 ? null : crime;
  }

  crimeOfStriking(figure: FigureId): number | null {
    const crime = this.sim.crime_of_striking(figure);

    return crime < 0 ? null : crime;
  }

  isWatchman(figure: FigureId): boolean {
    return this.sim.is_watchman(figure);
  }

  lifeStage(figure: FigureId): number {
    // Straight through and uncached, like {@link WasmWitnessSource.isAdult}
    // beside it: the answer is derived from a birth date and the clock sim-side
    // (`sim_core`'s `Snapshot::life_stage`), so it is a division rather than a
    // list, and the one caller asks it once a second over a roster of a dozen
    // (`dev/debuglog.ts`).
    return this.sim.life_stage(figure);
  }

  isAdult(figure: FigureId): boolean {
    // Straight through and uncached, like every other scalar on this surface:
    // the answer is derived from a birth date and the clock sim-side
    // (`sim_core`'s `Snapshot::is_adult`), so it is a division rather than a
    // list, and the one caller asks it once a second over a roster of a dozen
    // (`dev/debuglog.ts`).
    return this.sim.is_adult(figure);
  }

  /*
   * The simulation's four **debug** reads (wave 1.6, chunk D).
   *
   * Not on {@link WitnessSource} and deliberately so: they satisfy
   * `dev/debuglog.ts`'s `LocalWatch` structurally, the same bargain the birth
   * door is handed over under (`witness/origins.ts`'s `BirthDoor`). A debug
   * surface must not widen a production interface — the hand-authored fixture
   * and the self-checks' stub sources implement `WitnessSource` in full, and
   * four members they could only answer with nothing would be four members
   * somebody has to keep answering forever.
   *
   * Uncached, all four, and none of them needs one: three are scalars and the
   * fourth answers an empty array on almost every call. What keeps them cheap is
   * the caller's cadence, which is once a second and only while a window is open
   * (`dev/debugpanel.ts`).
   */

  localUncoveredTicks(): number {
    return this.sim.uncovered_ticks();
  }

  localDemotionTicks(): number {
    return this.sim.demotion_ticks();
  }

  localNotesMade(): number {
    return this.sim.local_notes_made();
  }

  localNotesSince(made: number): readonly string[] {
    return this.sim.local_notes_since(made);
  }

  carried(): readonly CarriedItem[] {
    return itemRows(this.sim.carried());
  }

  worn(): readonly CarriedItem[] {
    return itemRows(this.sim.worn());
  }

  wearable(): number | null {
    const item = this.sim.wearable();

    return item === 0 ? null : item;
  }

  wielded(): number | null {
    // {@link WasmWitnessSource.wearable}'s encoding, one union arm over: zero is
    // the boundary's *"nothing here"* for an item handle and becomes `null` on
    // the way in, so nothing above this line carries the spelling
    // (`WasmSim::wielded`).
    const item = this.sim.wielded();

    return item === 0 ? null : item;
  }

  quiver(): { readonly bow: number | null; readonly arrows: number } {
    // Zero is the boundary's own *"nothing here"* for an item handle — no arena
    // slot is ever numbered zero — and it becomes `null` on the way in, so
    // nothing above this line has to know the encoding (`WasmSim::bow`).
    const bow = this.sim.bow();

    return { bow: bow === 0 ? null : bow, arrows: this.sim.arrows() };
  }

  tileItems(pos: TilePos): readonly CarriedItem[] {
    return itemRows(this.sim.tile_items(pos.x, pos.y, pos.z));
  }

  itemLabel(item: number): string {
    return this.sim.item_label(item);
  }

  itemName(item: number): string {
    return this.sim.item_name(item);
  }

  relicLines(item: number): readonly string[] {
    return this.sim.relic_lines(item);
  }

  relics(): readonly CarriedItem[] {
    return itemRows(this.sim.relics());
  }

  itemPortable(kind: number): boolean {
    // Uncached like {@link WasmWitnessSource.lotName}, and for the same reason:
    // it is a scalar off a ten-row table, and the caller asks it when a watched
    // tile is re-read rather than while a frame is being assembled
    // (`witness/witness.ts`'s `repile`). A `Map` here would be a second copy of
    // the table with a cheaper excuse.
    return item_portable(kind);
  }

  lots(settlement: SettlementId): readonly TownLot[] {
    const flat = this.sim.lots(settlement);
    const standing: TownLot[] = [];

    for (let i = 0; i + LOT_STRIDE - 1 < flat.length; i += LOT_STRIDE) {
      standing.push({
        kind: flat[i] ?? 0,
        trade: handleOr(flat[i + 1] ?? NONE),
        doorstep: { x: flat[i + 2] ?? 0, y: flat[i + 3] ?? 0 },
        work: { x: flat[i + 4] ?? 0, y: flat[i + 5] ?? 0 },
      });
    }

    return standing;
  }

  lotName(kind: number): string {
    return lot_kind_name(kind);
  }

  jobWord(kind: number): string {
    // Uncached like {@link WasmWitnessSource.lotName} beside it and for its
    // reason: a scalar off an eleven-row table, asked when a board is opened rather
    // than while a frame is being assembled. A `Map` here would be a second copy
    // of the table with a cheaper excuse (`job_kind_name`).
    return job_kind_name(kind);
  }

  shelved(pos: TilePos): readonly ShelvedBook[] {
    const flat = this.sim.shelved(pos.x, pos.y, pos.z);
    const books: ShelvedBook[] = [];

    for (let i = 0; i + SHELF_STRIDE - 1 < flat.length; i += SHELF_STRIDE) {
      books.push({ item: flat[i] ?? 0, citations: flat[i + 1] ?? 0 });
    }

    return books;
  }

  hasShelf(pos: TilePos): boolean {
    return this.sim.has_shelf(pos.x, pos.y, pos.z);
  }

  workAt(pos: TilePos): number | null {
    const kind = this.sim.work_at(pos.x, pos.y, pos.z);

    // `-1` rather than `undefined` across the wall, because this is a read a
    // client makes on a keystroke and a nullable allocates (`WasmSim::work_at`).
    return kind < 0 ? null : kind;
  }

  openGround(
    settlement: SettlementId,
    columns: readonly (readonly [number, number])[],
  ): readonly (readonly [number, number] | null)[] {
    if (columns.length === 0) {
      return [];
    }

    const asked = new Int32Array(columns.length * 2);

    for (const [slot, [x, y]] of columns.entries()) {
      asked[slot * 2] = x;
      asked[slot * 2 + 1] = y;
    }

    const flat = this.sim.open_ground(settlement, asked);
    const found: (readonly [number, number] | null)[] = [];

    for (let i = 0; i + OPEN_GROUND_STRIDE - 1 < flat.length; i += OPEN_GROUND_STRIDE) {
      // The `found` column first, because there is no coordinate free to mean
      // "nowhere": every pair is a real column somewhere in the world
      // (`sim_wasm::flatten`'s `open_ground`).
      found.push(flat[i] === 1 ? [flat[i + 1] ?? 0, flat[i + 2] ?? 0] : null);
    }

    return found;
  }

  /**
   * Turns a `Spoken` handle into the client's own value, and frees it.
   *
   * The line, the speaker's label and the citation are copied out and the
   * wasm-bindgen object goes immediately: one of these is created per
   * interaction, and "eventually" is not a budget (`crates/sim-wasm`).
   */
  private said(spoken: ReturnType<WasmSim["speak"]>): SpokenLine | null {
    if (spoken === undefined) {
      return null;
    }

    const line: SpokenLine = {
      figure: spoken.figure === -1 ? null : spoken.figure,
      speaker: spoken.speaker,
      // Whole, exactly as `sim-core` composed it. Nothing in this client is
      // allowed to build a sentence about a record (plan decision 9).
      text: spoken.text,
      about: spoken.about,
      hops: spoken.hops,
      learned: { year: spoken.learned_year, dayOfYear: spoken.learned_day_of_year },
    };

    spoken.free();

    return line;
  }

  /**
   * Decodes one settlement's shelf out of the flat layout.
   *
   * Split from {@link WasmWitnessSource.chronicles} because that method answers
   * two different questions — the visited town's books, which are cached, and
   * anywhere else's, which are not — and a decoder inlined in both would be the
   * stride written twice.
   */
  private shelf(settlement: SettlementId): readonly ChronicleView[] {
    const flat = this.sim.chronicles(settlement);
    const books: ChronicleView[] = [];

    for (let i = 0; i + CHRONICLE_STRIDE - 1 < flat.length; i += CHRONICLE_STRIDE) {
      const author = flat[i + 1] ?? 0;
      const x = flat[i + 3] ?? 0;
      const y = flat[i + 4] ?? 0;

      books.push({
        id: flat[i] ?? 0,
        author,
        // One name per book, pulled with the list: an author may be long dead
        // and is often not on the visited town's roster, so `roster()` cannot
        // answer for them — the same reason a caravan's talker carries theirs.
        authorName: this.named(author),
        settlement: flat[i + 2] ?? 0,
        // The book's own tile, z included: it is where it was *left* rather than
        // where the ground happens to be, which makes it the one placement on
        // this surface the client does not look up.
        position: { x, y, z: flat[i + 5] ?? 0 },
        left: { year: flat[i + 6] ?? 0, dayOfYear: flat[i + 7] ?? 0 },
        entries: flat[i + 8] ?? 0,
      });
    }

    return books;
  }

  /**
   * Reads the whole Book of Eras out of the boundary.
   *
   * Split from {@link WasmWitnessSource.saga} the way {@link shelf} is split
   * from `chronicles`: that method is the cache and this is the decode, and a
   * decoder inlined in an `??=` would be the strides written inside a cache
   * check.
   *
   * The era row carries the numbers and a count; the two names and the chapter
   * cross per call, one age at a time (decision 11's split). The **index** is
   * what every per-age read takes — never {@link SagaEra.ordinal}, which is the
   * age's true number in a world whose book has been cut.
   */
  private book(): readonly SagaEra[] {
    const flat = this.sim.saga_eras();
    const ages: SagaEra[] = [];

    for (let i = 0; i + SAGA_ERA_STRIDE - 1 < flat.length; i += SAGA_ERA_STRIDE) {
      const era = i / SAGA_ERA_STRIDE;
      const endedYear = flat[i + 4] ?? NONE;

      ages.push({
        ordinal: flat[i] ?? 0,
        anchor: flat[i + 1] ?? 0,
        began: { year: flat[i + 2] ?? 0, dayOfYear: flat[i + 3] ?? 0 },
        // The age the reader is standing in has no end, and the boundary says so
        // with the sentinel in both date columns rather than with a flag — the
        // same spelling a market row's absent shock uses.
        ended: endedYear === NONE ? null : { year: endedYear, dayOfYear: flat[i + 5] ?? 0 },
        title: this.sim.era_title(era),
        name: this.sim.era_name(era),
        lines: this.chapter(era),
      });
    }

    return ages;
  }

  /**
   * Reads one age's chapter, pairing every sentence with the records it
   * narrates.
   *
   * **The trace contract's client half** (Phase 6 decision O6). The two lists
   * arrive in the same order and the same length, so the zip is the whole of it;
   * a line whose group is missing would be a boundary that broke the pairing,
   * and it comes back citing nothing rather than citing somebody else's records
   * — which is the failure a consumer can see and act on
   * (`witness/saga.ts` refuses and counts those).
   *
   * The citation array is walked by its own per-line counts, because its stride
   * is variable ({@link SAGA_CITE_HEADER}). A count that ran past the end of the
   * array stops the walk rather than reading whatever is next: this is the one
   * layout on the surface that cannot be checked by a length, and a decoder that
   * kept going would hand a page the wrong records.
   */
  private chapter(era: number): readonly SagaLine[] {
    const said = this.sim.era_lines(era);
    const flat = this.sim.era_citations(era);
    const lines: SagaLine[] = [];
    let at = 0;

    for (const text of said) {
      const cites: EventId[] = [];
      let slot = NONE;

      if (at + SAGA_CITE_HEADER - 1 < flat.length) {
        slot = flat[at] ?? NONE;

        const count = flat[at + 1] ?? 0;
        const ends = at + SAGA_CITE_HEADER + count;

        if (ends <= flat.length) {
          for (let c = at + SAGA_CITE_HEADER; c < ends; c += 1) {
            cites.push(flat[c] ?? 0);
          }

          at = ends;
        } else {
          // A malformed group ends the walk: the rest of the array cannot be
          // located, so the remaining lines come back uncited rather than
          // mispaired.
          at = flat.length;
        }
      }

      lines.push({ slot, text, cites });
    }

    return lines;
  }

  /**
   * Decodes a claims array — a card's, or a figure's — out of the flat layout.
   *
   * One decoder for both reads, because it is one layout: the boundary answers
   * `origin_claims` and `claims` with the same rows
   * (`crates/sim-wasm/src/flatten.rs`), and a second copy of a four-column walk
   * is a second place to write `row + 2`.
   *
   * The parent's name is pulled with the row, like a book's author and a card's
   * mother: a seat's holder may be long dead and is rarely on the visited town's
   * roster.
   */
  private seats(flat: Float64Array): readonly OriginClaim[] {
    const held: OriginClaim[] = [];

    for (let i = 0; i + CLAIM_STRIDE - 1 < flat.length; i += CLAIM_STRIDE) {
      const parent = flat[i] ?? 0;
      const settlement = flat[i + 1] ?? 0;

      held.push({
        parent,
        parentName: this.named(parent),
        settlement,
        settlementName: this.sim.settlement_name(settlement),
        position: flat[i + 2] ?? NONE,
        // The record that seated them, which is what makes a claim traceable
        // rather than a label: the same `EventId` the log narrates.
        cites: flat[i + 3] ?? 0,
      });
    }

    return held;
  }

  /**
   * Returns what to write over somebody's head: their name, and the byname the
   * log earned them.
   *
   * Two calls rather than one, because that is the shape the boundary has —
   * strings cross per call — and a *label* rather than a sentence, which is why
   * composing it here is allowed at all: the epithet is `sim-core`'s string and
   * the comma is the whole of this client's contribution. The same pair a grave
   * is cut with (`speech::inscription`), so a man is not buried under a name
   * nobody in the street used.
   */
  private named(figure: FigureId): string {
    const name = this.sim.figure_name(figure);
    const byname = this.sim.figure_byname(figure);

    return byname === "" ? name : `${name}, ${byname}`;
  }

  /** The settlements, pulled once per stamp: a town is founded about once a decade. */
  private towns(): readonly SettlementView[] {
    const cache = this.freshWorld();

    if (cache.towns === null) {
      const flat = this.sim.settlements();
      const towns: SettlementView[] = [];

      for (let i = 0; i + SETTLEMENT_STRIDE - 1 < flat.length; i += SETTLEMENT_STRIDE) {
        const settlement = flat[i] ?? 0;

        towns.push({
          settlement,
          // One string per settlement, pulled with the list rather than on every
          // label draw: sixteen calls once a day is not a per-frame path.
          name: this.sim.settlement_name(settlement),
          x: flat[i + 1] ?? 0,
          y: flat[i + 2] ?? 0,
        });
      }

      cache.towns = towns;
    }

    return cache.towns;
  }

  /**
   * Returns the map cache, emptied first if the world has moved on since it was
   * filled.
   *
   * The stamp is the whole key: a settlement is founded on a day boundary and
   * writes a record when it is, so a stamp that has not moved is a map that has
   * not changed — wherever the player has walked to in the meantime.
   */
  private freshWorld(): WasmWitnessSource["worldCache"] {
    const stamp = this.world.stamp();

    if (stamp !== this.worldCache.stamp) {
      this.worldCache = { stamp, towns: null, routes: null, ruins: null, saga: null };
    }

    return this.worldCache;
  }

  /**
   * Returns the nearest settlement to where the client last said the player was,
   * or `-1` in a world with no towns.
   *
   * # A TEXTURE KEY, and never a place claim
   *
   * This is the arithmetic {@link WasmWitnessSource.here} used to be, kept for
   * exactly three reads and forbidden to everything else (owner-ruled
   * 2026-08-28, amending item 6's rider 4 by one clause). What it answers is
   * **which corner of the world to look in** — the simulation's aftermath,
   * flight and pulse reads all take a settlement handle as the centre of a
   * radius — and it is not, and must never be read as, an answer to *"which town
   * is the player in"*. That question has one answer, it is the promotion slot,
   * and it can say *nowhere* (`world/types.ts`, {@link WitnessSource.here}).
   *
   * The line between the two is what the whole of item 6 was about, so it is
   * worth stating as a rule rather than a habit: **if the answer decides what a
   * player may do, it is `here()`; if it only decides where to look, it is
   * this.** The market panel, the roster, the share-word offer, the town board,
   * the beats, the shelf, the witness layer's `settlement` and the debug window
   * are all the first kind. The three lists in
   * {@link WasmWitnessSource.nearCache} are the second, and there is nothing
   * else on this surface that is.
   *
   * Private, and that is half the guard: nothing outside this file can reach it,
   * so nothing above `world/` can accidentally acquire a second opinion about
   * where the player is standing.
   *
   * Sixteen squared distances over an already-cached list, paid on a day
   * boundary rather than per frame — the same cost {@link WitnessSource.here}
   * used to be.
   */
  private nearestTown(): SettlementId {
    const tile = this.world.playerTile();
    const x = tile?.x ?? 0;
    const y = tile?.y ?? 0;
    let best: SettlementId = -1;
    let bestDistance = Number.POSITIVE_INFINITY;

    for (const town of this.towns()) {
      const distance = (town.x - x) ** 2 + (town.y - y) ** 2;

      if (distance < bestDistance) {
        bestDistance = distance;
        best = town.settlement;
      }
    }

    return best;
  }

  /**
   * Returns the road's cache, emptied first if the world has moved on **or the
   * player has walked nearer another town**.
   *
   * {@link WasmWitnessSource.freshHere}'s twin over the other key
   * ({@link WasmWitnessSource.nearestTown}), and it drops for the same reason
   * that one does: the three lists under it are read around a settlement's
   * centre, so crossing the midpoint between two towns is the moment the answer
   * changes. It never drops to *empty* — walking out of a town does not empty
   * the road, it hands the road to the town at the other end of it.
   */
  private freshNear(): WasmWitnessSource["nearCache"] {
    const stamp = this.world.stamp();
    const near = this.nearestTown();

    if (stamp !== this.nearCache.stamp || near !== this.nearCache.near) {
      this.nearCache = { stamp, near, features: null, groups: null, pulses: null };
    }

    return this.nearCache;
  }

  /**
   * Returns the visited settlement's cache, emptied first if the world has
   * moved on **or the player has**.
   *
   * Decision 10a's fix, under item 6's answer to what `here()` is. The key is
   * the **promotion slot**, so it changes on the tick the simulation hands the
   * tier to another town — and every list in this cache is a read *about* a
   * settlement. Without the second half of the key, crossing between two towns
   * served the one behind you until the calendar happened to turn — a whole
   * in-game day of standing in a street full of other people's neighbours, and
   * since Phase 5 lengthened the day it would be most of a session.
   *
   * Nowhere is a key like any other: a session before its first tick, and a
   * player out past every town's halo, drop the lists and read nothing. That is
   * right for everything under *this* key and wrong for the road, which is why
   * the road has one of its own ({@link WasmWitnessSource.freshNear}).
   *
   * It costs a `here()` per bulk read, which since item 6 is one scalar across
   * the boundary — cheaper than the query it guards, and paid on a day boundary
   * rather than per frame.
   */
  private freshHere(): WasmWitnessSource["hereCache"] {
    const stamp = this.world.stamp();
    const at = this.here();

    if (stamp !== this.hereCache.stamp || at !== this.hereCache.at) {
      this.hereCache = { stamp, at, roster: null, chronicles: null, beats: null };
    }

    return this.hereCache;
  }

  /**
   * Returns the session-stable id for the feature a record and a kind name.
   *
   * A counter rather than an encoding of the pair, because the contract says a
   * number a prop can be keyed by and says nothing about what it means — and a
   * client that packed two ids into one would have invented a format at the
   * boundary. The same pair always gets the same number back, so re-pulling the
   * aftermath after a day boundary does not re-key every prop in the world.
   */
  private featureId(event: EventId, kind: number): number {
    const key = `${event}:${kind}`;
    const known = this.featureIds.get(key);

    if (known !== undefined) {
      return known;
    }

    const id = this.featureIds.size + 1;

    this.featureIds.set(key, id);
    this.featureRefs.set(id, { event, kind });

    return id;
  }
}

/**
 * Reads a presence column: the handle it holds, or `null` where the simulation
 * said nobody.
 *
 * {@link NONE} is the one spelling of *absent* on this boundary and it cannot
 * collide, because every value it stands in for is a non-negative id
 * (`crates/sim-wasm/src/flatten.rs`). A column that is missing entirely — a row
 * cut short, which nothing produces — reads as absent too, and that is the safe
 * direction: an absent handle is a card the flow cannot press a door with, where
 * an `undefined` allowed through would be a birth queued against whatever that
 * became on the way over.
 */
function handleOr(value: number | undefined): number | null {
  return value === undefined || value === NONE ? null : value;
}

/**
 * Decodes an item layout — the same four columns at both ends of it.
 *
 * One reader for {@link WasmWitnessSource.carried} and
 * {@link WasmWitnessSource.tileItems} because the boundary hands them over at
 * one stride (`crates/sim-wasm/src/flatten.rs`): a pile on the ground and a row
 * in the inventory are the same four facts about the same kind of thing, and two
 * decoders would be two places for the stride to drift.
 */
function itemRows(flat: Float64Array): readonly CarriedItem[] {
  const items: CarriedItem[] = [];

  for (let i = 0; i + ITEM_STRIDE - 1 < flat.length; i += ITEM_STRIDE) {
    items.push({
      item: flat[i] ?? 0,
      kind: flat[i + 1] ?? 0,
      material: flat[i + 2] ?? 0,
      quality: flat[i + 3] ?? 0,
    });
  }

  return items;
}

/**
 * Returns the settlement nearest a tile, or `null` in a world with none.
 *
 * Squared distance over the whole list, which is sixteen rows in the reference
 * world: a spatial index for sixteen points would be a data structure standing
 * in for one comparison each.
 */
function nearestSettlement(sim: WasmSim, x: number, y: number): SettlementView | null {
  const flat = sim.settlements();
  let best: SettlementView | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (let i = 0; i + SETTLEMENT_STRIDE - 1 < flat.length; i += SETTLEMENT_STRIDE) {
    const settlement = flat[i] ?? 0;
    const townX = flat[i + 1] ?? 0;
    const townY = flat[i + 2] ?? 0;
    const distance = (townX - x) ** 2 + (townY - y) ** 2;

    if (distance < bestDistance) {
      bestDistance = distance;
      // The name is pulled for the winner only, and only once the loop is done
      // with it: this runs before there is a witnessing source to cache
      // anything, and a string per candidate would be sixteen calls to choose
      // one spawn point.
      best = { settlement, name: "", x: townX, y: townY };
    }
  }

  return best === null ? null : { ...best, name: sim.settlement_name(best.settlement) };
}

/**
 * Returns which way a prop faces, in radians.
 *
 * **Cosmetic, and the client's alone.** The simulation has no opinion about
 * which way a gravestone is turned — `aftermath` places a `(kind, position,
 * citation)` triple and says so — but a row of identically-oriented stones reads
 * as a texture rather than as a place. So the angle is derived from the record
 * the feature cites, which makes it stable for the life of the world and
 * different for every prop, the same way `render/props.ts` picks a stone's
 * silhouette from its id.
 *
 * A market board faces 0 so its notices are towards a player walking up from the
 * middle of town (`render/props.ts`: a prop's geometry faces +y).
 */
function facingOf(event: EventId, kind: number): number {
  if (kind === FEATURE_MARKET_BOARD) {
    return 0;
  }

  // The golden angle again, for the reason `witness/witness.ts` uses it to place
  // a crowd: consecutive multiples of it never line up, so consecutive records
  // do not produce a row of parallel stones.
  return ((event * GOLDEN_ANGLE) % TAU) - Math.PI;
}

/**
 * Reads the bootstrap years out of the URL, or returns the default.
 *
 * `?bootstrap=0` is a world with no past — the Phase 1 dig prototype — and
 * anything larger is that many years of history built before the first tick
 * ({@link DEFAULT_BOOTSTRAP_YEARS} for what it costs). A malformed or
 * out-of-range value throws for {@link seedFromLocation}'s reason: a session
 * that quietly played a different world than the URL asked for is an
 * unreproducible bug report.
 */
function bootstrapFromLocation(): number {
  const raw = new URLSearchParams(window.location.search).get(BOOTSTRAP_PARAM);

  if (raw === null || raw.trim() === "") {
    return DEFAULT_BOOTSTRAP_YEARS;
  }

  const years = Number(raw.trim());

  if (!Number.isInteger(years) || years < 0 || years > MAX_BOOTSTRAP_YEARS) {
    throw new Error(
      `?${BOOTSTRAP_PARAM}=${raw} is not a whole number of years in 0..=${MAX_BOOTSTRAP_YEARS}`,
    );
  }

  return years;
}

/**
 * Reads the seed out of the URL, or returns the default.
 *
 * A `BigInt`, because a seed is a full `u64` and a JavaScript number is not
 * (`WasmSim`'s constructor takes one for the same reason). Decimal or `0x` hex,
 * so a hash printed in the HUD or a replay file can be pasted back in. A
 * malformed value throws rather than falling back: silently playing a different
 * world than the URL asked for is how an unreproducible bug report is written.
 */
function seedFromLocation(): bigint {
  const raw = new URLSearchParams(window.location.search).get(SEED_PARAM);

  if (raw === null || raw.trim() === "") {
    return DEFAULT_SEED;
  }

  try {
    return BigInt(raw.trim()) & 0xffff_ffff_ffff_ffffn;
  } catch {
    throw new Error(`?${SEED_PARAM}=${raw} is not an integer`);
  }
}

/**
 * Checks the numbers the client keeps its own copy of against the simulation's.
 *
 * `CHUNK_SIZE` and `TILE_SHAPE_RAMP` are stated in `world/types.ts` because tile
 * and chunk arithmetic happens all over the client and importing them from wasm
 * would put an async boundary in front of a constant. The generated package
 * expects exactly this check to be made rather than trusted (`chunk_size`'s and
 * `ramp_shape_id`'s documentation), and it is worth making loudly: neither
 * number would crash if it drifted. A chunk edge would draw the world in the
 * wrong place, and a shape id would quietly stop the player from finding a
 * single ramp.
 */
function checkConstants(): void {
  const simChunkSize = chunk_size();

  if (simChunkSize !== CHUNK_SIZE) {
    throw new Error(
      `the simulation's chunk edge is ${simChunkSize} but the client is built for ${CHUNK_SIZE}`,
    );
  }

  const simRampShape = ramp_shape_id();

  if (simRampShape !== TILE_SHAPE_RAMP) {
    throw new Error(
      `the simulation's ramp shape is ${simRampShape} but the client is built for ${TILE_SHAPE_RAMP}`,
    );
  }

  const simStairShape = stair_shape_id();

  if (simStairShape !== TILE_SHAPE_STAIR_UP) {
    throw new Error(
      `the simulation's stair shape is ${simStairShape} but the client is built for ${TILE_SHAPE_STAIR_UP}`,
    );
  }

  if (world_z_min() >= world_z_max()) {
    throw new Error(`the simulation reports an empty z band: ${world_z_min()}..${world_z_max()}`);
  }

  checkFeatureKinds();
  checkBeatSurfaces();
  checkPlayerLimits();
  checkSagaBounds();
  checkOriginDoors();
  checkLocalTier();
}

/**
 * Checks the client's copies of the local tier's vocabularies and its one cap.
 *
 * {@link checkBeatSurfaces}'s shape for Phase 7, with a milder symptom and the
 * same reasoning. **Both halves matter**: the ids alone would pass a build in
 * which the simulation kept eight job kinds and reordered them, and every scene
 * would still decode while a woodcutter swung at a seam of rock. So each id is
 * checked against the word the simulation gives it.
 *
 * **No building id is checked, because the client names none.** A workshop is
 * offered where the lot's own `trade` column says somebody works there and it is
 * labelled with the word `lot_kind_name` answers with, so there is nothing on
 * this side to drift — which is the arrangement every vocabulary would have if
 * the client never had to switch on one.
 *
 * {@link MAX_CARRIED} is checked for {@link checkPlayerLimits}' reason and with
 * its symptom: nothing crashes if it drifts, the simulation enforces the real
 * bound whatever this client believes, and a stale copy would let the inventory
 * promise room the hands do not have.
 *
 * **One claim here is not about a copy at all**: the portability table has no
 * client-side twin to compare, so what is checked is that the read still
 * distinguishes — see below.
 */
function checkLocalTier(): void {
  const jobs = Array.from(job_kind_ids());
  const expected: readonly (readonly [number, string])[] = [
    [JOB_MINE, "mine"],
    [JOB_FELL, "fell"],
    [JOB_HARVEST, "harvest"],
    [JOB_FORGE, "forge"],
    [JOB_CARVE, "carve"],
    [JOB_COOK, "cook"],
    [JOB_WRITE, "write"],
    [JOB_TRANSCRIBE, "transcribe"],
    [JOB_PATROL, "patrol"],
    [JOB_BURY, "bury"],
    [JOB_INTER, "inter"],
  ];

  for (const [id, meaning] of expected) {
    if (!jobs.includes(id)) {
      throw new Error(
        `the simulation's job kinds are [${jobs.join(", ")}] but the client is built for` +
          ` [${expected.map(([known]) => known).join(", ")}]`,
      );
    }

    const named = job_kind_name(id);

    if (named !== meaning) {
      throw new Error(
        `the client draws job kind ${id} as ${meaning} but the simulation calls it` +
          ` ${named === "" ? "nothing at all" : named}`,
      );
    }
  }

  const items: readonly (readonly [number, string])[] = [
    [ITEM_BAR, "bar"],
    [ITEM_BOULDER, "boulder"],
    [ITEM_WOOD, "log"],
    [ITEM_PLANT, "plant"],
    [ITEM_FOOD, "meal"],
    [ITEM_CORPSE, "body"],
  ];

  for (const [id, meaning] of items) {
    const named = item_kind_name(id);

    if (named !== meaning) {
      throw new Error(
        `the client draws item kind ${id} as a ${meaning} but the simulation calls it` +
          ` ${named === "" ? "nothing at all" : `a ${named}`}`,
      );
    }
  }

  /*
   * And the four stock bands, which chunk U-2 gave the market board a colour for
   * (`hud/panels/market.ts`'s `bandTone`). The same both-halves check with the
   * mildest symptom on this page: nothing crashes if the ids reorder, and what a
   * reader would see is a famine painted in the colour of a full granary — a
   * board that looks fine and says the opposite of what the town is feeling.
   *
   * There is no `stock_band_ids()` beside `job_kind_ids()` above, so the ids are
   * checked one at a time against the word each one means, which is the half
   * that matters anyway.
   */
  const bands: readonly (readonly [number, string])[] = [
    [BAND_FAMINE, "famine"],
    [BAND_DEAR, "dear"],
    [BAND_STEADY, "steady"],
    [BAND_PLENTY, "plenty"],
  ];

  for (const [id, meaning] of bands) {
    const named = stock_band_name(id);

    if (named !== meaning) {
      throw new Error(
        `the client colours stock band ${id} as ${meaning} but the simulation calls it` +
          ` ${named === "" ? "nothing at all" : named}`,
      );
    }
  }

  /*
   * The portability table, checked for its *shape* rather than row by row
   * (chunk B). The client keeps no copy of it — that is the whole point of
   * `WitnessSource.itemPortable` — so there is nothing here to compare against,
   * and what is worth catching instead is a read that has stopped saying
   * anything: an export wired to a constant answers every pile the same way, and
   * both constants are a shipped bug. `true` everywhere is `F — pick up brick
   * table` over every bench again, and `false` everywhere is a world nothing can
   * be picked up in.
   */
  if (!item_portable(ITEM_BOULDER)) {
    throw new Error(
      "the simulation says a boulder is not something a pair of hands lifts, so nothing the dig" +
        " verb leaves could be picked up",
    );
  }

  if (!Array.from(item_kind_ids()).some((kind) => !item_portable(kind))) {
    throw new Error(
      "the simulation says every kind of item lifts, so a workshop's own table would be offered" +
        " as a pick-up the world then refuses in silence",
    );
  }

  const simCarried = max_carried();

  if (simCarried !== MAX_CARRIED) {
    throw new Error(
      `the simulation lets one pair of hands carry ${simCarried} things but the client is built` +
        ` for ${MAX_CARRIED}`,
    );
  }

  // The two numbers a swing is measured by, and they are checked here for a
  // sharper version of the cap's reason: the simulation refuses a swing out of
  // reach or inside the cooldown **in silence**, so a stale copy on this side
  // is a strike prompt over somebody the player cannot hit and a key that does
  // nothing (the graybox ruling's own example of a verb a player stops
  // pressing).
  const simReach = strike_reach_tiles();

  if (simReach !== STRIKE_REACH_TILES) {
    throw new Error(
      `the simulation lets a blow reach ${simReach} tiles but the client is built for` +
        ` ${STRIKE_REACH_TILES}`,
    );
  }

  const simCooldown = strike_cooldown_ticks();

  if (simCooldown !== STRIKE_COOLDOWN_TICKS) {
    throw new Error(
      `the simulation makes a hand wait ${simCooldown} ticks between swings but the client is` +
        ` built for ${STRIKE_COOLDOWN_TICKS}`,
    );
  }

  // And the bow's two, on exactly the same terms (wave 2D-b): a shot out of
  // range or inside the cooldown is refused in silence, so a stale copy here is
  // a loose prompt over somebody the arrow cannot reach.
  const simRange = bow_range_tiles();

  if (simRange !== BOW_RANGE_TILES) {
    throw new Error(
      `the simulation lets an arrow carry ${simRange} tiles but the client is built for` +
        ` ${BOW_RANGE_TILES}`,
    );
  }

  const simLoose = loose_cooldown_ticks();

  if (simLoose !== LOOSE_COOLDOWN_TICKS) {
    throw new Error(
      `the simulation makes a hand wait ${simLoose} ticks between shots but the client is built` +
        ` for ${LOOSE_COOLDOWN_TICKS}`,
    );
  }

  // And wave 2F's two, on the cap's terms rather than the swing's: the
  // simulation refuses a fifth companion and a beckon out of reach in silence,
  // so a stale copy here is a prompt over somebody the world will not let the
  // player take along.
  const simCompanions = max_companions();

  if (simCompanions !== MAX_COMPANIONS) {
    throw new Error(
      `the simulation lets a hero keep ${simCompanions} companions but the client is built for` +
        ` ${MAX_COMPANIONS}`,
    );
  }

  const simBeckon = beckon_reach_tiles();

  if (simBeckon !== BECKON_REACH_TILES) {
    throw new Error(
      `the simulation takes somebody along from ${simBeckon} tiles but the client is built for` +
        ` ${BECKON_REACH_TILES}`,
    );
  }

  // And chunk Fx-5's, which is the one number here that bounds a *wait* rather
  // than narrowing an offer. Its symptom if it drifts is the loudest of the lot
  // and is what it replaces: a guard measured on this side of the wall in units
  // the simulation does not keep sat at a thousand world days and never fired,
  // so a body that was never getting up held the screen until the tab was
  // closed. Short is as bad as long in the other direction — a player woken a
  // morning before the wound would have let them up.
  const simHealing = heal_days_at_slowest();

  if (simHealing !== HEAL_DAYS_AT_SLOWEST) {
    throw new Error(
      `the simulation's slowest wound takes ${simHealing} days to heal but the client is built` +
        ` for ${HEAL_DAYS_AT_SLOWEST}`,
    );
  }
}

/**
 * Checks the client's copies of the two bounds the Book of Eras is laid out
 * against.
 *
 * {@link checkPlayerLimits}'s twin for Phase 6. Wave 2B shipped it **weaker on
 * purpose** — `sim-core` kept both numbers private to its saga module, so
 * `crates/sim-wasm` transcribed them and this compared one transcription against
 * the other — and wave 3A published both, so what it compares now is this
 * client's copy against the simulation's own constant, exactly as the player
 * caps are compared.
 *
 * Neither would crash if it drifted, which is why it is checked loudly: a stale
 * {@link MAX_SAGA_ERAS} lays the book out shorter than the world's history and
 * reads as a quiet world, and a stale {@link MAX_SAGA_LINE_CITES} sizes a trace
 * row that then has a record too many for it.
 */
function checkSagaBounds(): void {
  const simEras = max_saga_eras();

  if (simEras !== MAX_SAGA_ERAS) {
    throw new Error(
      `the simulation keeps ${simEras} ages in a saga but the client is built for` +
        ` ${MAX_SAGA_ERAS}`,
    );
  }

  const simCites = max_saga_line_cites();

  if (simCites !== MAX_SAGA_LINE_CITES) {
    throw new Error(
      `the simulation narrates up to ${simCites} records per saga line but the client is built` +
        ` for ${MAX_SAGA_LINE_CITES}`,
    );
  }
}

/**
 * Checks the client's copies of the three doors a player can be born through,
 * and the two bounds the birth screen is laid out against.
 *
 * {@link checkBeatSurfaces} and {@link checkSagaBounds} in one function, because
 * the birth screen is one surface and both halves of it are checked for the same
 * reason — with a sharper edge than either. A beat surface that renumbered would
 * stage a body where a price moved; an **origin kind** that renumbered would
 * send a bound child's figure number through
 * {@link WorldSource.queueBornOrphan}'s settlement argument, and the simulation
 * would refuse that birth *in silence*, because a tick has no error channel. The
 * symptom would be a start screen where nothing happens when you press the
 * button.
 *
 * So the ids are checked against the word each one means, which is the thing
 * that cannot be reordered quietly, and the two caps are compared against the
 * core's own constants.
 */
function checkOriginDoors(): void {
  const doors = Array.from(origin_kind_ids());
  const expected: readonly (readonly [number, string])[] = [
    [ORIGIN_ORPHAN, "orphan"],
    [ORIGIN_UNRECORDED, "unrecorded"],
    [ORIGIN_BOUND, "bound"],
    [ORIGIN_HEIR, "heir"],
  ];

  for (const [id, meaning] of expected) {
    if (!doors.includes(id)) {
      throw new Error(
        `the simulation's birth doors are [${doors.join(", ")}] but the client is built for` +
          ` [${expected.map(([known]) => known).join(", ")}]`,
      );
    }

    const named = origin_kind_name(id);

    if (named !== meaning) {
      throw new Error(
        `the client sends birth door ${id} to the ${meaning} command but the simulation calls it` +
          ` the ${named === "" ? "nothing at all" : named}`,
      );
    }
  }

  const simOrigins = max_origins();

  if (simOrigins !== MAX_ORIGINS) {
    throw new Error(
      `the simulation offers ${simOrigins} lives at a birth but the client is built for` +
        ` ${MAX_ORIGINS}`,
    );
  }

  const simClaims = max_claims();

  if (simClaims !== MAX_CLAIMS) {
    throw new Error(
      `the simulation carries ${simClaims} claims per life but the client is built for` +
        ` ${MAX_CLAIMS}`,
    );
  }
}

/**
 * Checks the client's copies of the two caps a player's own verbs are bounded
 * by.
 *
 * {@link checkFeatureKinds}'s twin for Phase 5, and the reason is the same shape
 * with a different symptom. Nothing crashes if either number drifts: the
 * simulation enforces both whatever the client believes, so a stale
 * {@link MAX_CHRONICLE_ENTRIES} would let a player compile a book, place it, and
 * find a page missing from it afterwards — and a stale
 * {@link MAX_PLAYER_NAME_BYTES} would let them type a name that comes back
 * shorter than they wrote it. Both are the kind of wrong that reads as the game
 * being flaky rather than as a bug.
 *
 * Checked at startup rather than trusted, exactly as the chunk edge and the ramp
 * shape are, because both live behind an async boundary and neither belongs in
 * two places (`crates/sim-wasm/src/lib.rs`).
 */
function checkPlayerLimits(): void {
  const simEntries = max_chronicle_entries();

  if (simEntries !== MAX_CHRONICLE_ENTRIES) {
    throw new Error(
      `the simulation keeps ${simEntries} records per chronicle but the client is built for` +
        ` ${MAX_CHRONICLE_ENTRIES}`,
    );
  }

  const simName = max_player_name_bytes();

  if (simName !== MAX_PLAYER_NAME_BYTES) {
    throw new Error(
      `the simulation keeps ${simName} bytes of a typed name but the client is built for` +
        ` ${MAX_PLAYER_NAME_BYTES}`,
    );
  }
}

/**
 * Checks the client's aftermath kind ids against the simulation's.
 *
 * `world/types.ts` states {@link FEATURE_GRAVE}, {@link FEATURE_BURNED_FARMSTEAD}
 * and {@link FEATURE_BATTLEFIELD} so that `render/props.ts` can switch geometry
 * on a number without an async boundary in front of it — and wave 1B wrote them
 * down as a *guess* at an enum `sim-core` had not grown yet, with a note that
 * 4A must check them rather than trust them. This is that check.
 *
 * It is worth making loudly for the reason the ramp shape's is: nothing would
 * crash if a kind renumbered. A grave would be drawn where a farmstead burned,
 * and the only symptom would be a screenshot that looked slightly wrong.
 *
 * {@link FEATURE_MARKET_BOARD} is checked from the other side. It is the
 * client's own id for a prop the log never placed — the market's physical read —
 * so the failure to guard against is the simulation *taking* that number for a
 * fourth kind of aftermath.
 */
function checkFeatureKinds(): void {
  const kinds = Array.from(feature_kind_ids());
  const expected = [FEATURE_GRAVE, FEATURE_BURNED_FARMSTEAD, FEATURE_BATTLEFIELD];

  for (const kind of expected) {
    if (!kinds.includes(kind)) {
      throw new Error(
        `the simulation's aftermath kinds are [${kinds.join(", ")}] but the client is built for` +
          ` [${expected.join(", ")}]`,
      );
    }
  }

  if (kinds.includes(FEATURE_MARKET_BOARD)) {
    throw new Error(
      `the simulation now has an aftermath kind ${FEATURE_MARKET_BOARD}, which is the id this` +
        ` client draws a market board with (world/types.ts)`,
    );
  }
}

/**
 * Checks the client's beat surface ids against the simulation's — and the word
 * each one is supposed to mean.
 *
 * {@link checkFeatureKinds}'s twin for Phase 4 decision 1, and the reason it
 * exists is the same: `world/types.ts` states {@link BEAT_ROAD},
 * {@link BEAT_BOARD}, {@link BEAT_MOUTH} and {@link BEAT_GROUND} so
 * `witness/staging.ts` can switch on a number without an async boundary in front
 * of a constant, and wave 1B wrote them down as a **guess** at a projection
 * `sim-core` had not grown yet.
 *
 * **Both halves matter.** The ids alone would pass a build in which the
 * simulation kept four surfaces and reordered them — every beat would still
 * arrive, every switch would still match, and the client would stage a body on a
 * road where a price had moved on a board. So each id is checked against the
 * name `spotlight` gives it, which is the thing that cannot be reordered
 * silently.
 */
function checkBeatSurfaces(): void {
  const surfaces = Array.from(beat_surface_ids());
  const expected: readonly (readonly [number, string])[] = [
    [BEAT_ROAD, "road"],
    [BEAT_BOARD, "board"],
    [BEAT_MOUTH, "mouth"],
    [BEAT_GROUND, "ground"],
  ];

  for (const [id, meaning] of expected) {
    if (!surfaces.includes(id)) {
      throw new Error(
        `the simulation's beat surfaces are [${surfaces.join(", ")}] but the client is built for` +
          ` [${expected.map(([known]) => known).join(", ")}]`,
      );
    }

    const named = beat_surface_name(id);

    if (named !== meaning) {
      throw new Error(
        `the client draws beat surface ${id} as the ${meaning} but the simulation calls it` +
          ` the ${named === "" ? "nothing at all" : named}`,
      );
    }
  }
}
